/**
 * Command Schemas
 *
 * Defines the semantic structure of each hyperscript command.
 * Used by the pattern generator to create language-specific patterns.
 */

import type {
  CommandSchema as GrammarCommandSchema,
  RoleSpec as GrammarRoleSpec,
} from '@lokascript/framework';
import type { SemanticRole, ActionType, SemanticValue, PatternToken } from '../types';
import type { RoleMarker } from './profiles/types';
import {
  ELEMENT_TARGET_TYPES,
  GET_SOURCE_TYPES,
  PUT_DESTINATION_TYPES,
  PUT_VALUE_TYPES,
  SET_VALUE_TYPES,
  SET_DESTINATION_TYPES,
  SET_SCOPE_TYPES,
  EVENT_NAME_TYPES,
  DURATION_TYPES,
  FETCH_SOURCE_TYPES,
  FETCH_RESPONSE_TYPES,
  FETCH_OPTIONS_TYPES,
  VISIBILITY_STYLE_TYPES,
  FOR_VARIABLE_TYPES,
  FOR_SOURCE_TYPES,
  VIEW_TRANSITION_STYLE_TYPES,
  CONDITION_TYPES,
  APPEND_DESTINATION_TYPES,
} from './role-types';

// =============================================================================
// Command Schema Types
// =============================================================================

/**
 * A role specification in a command schema: the one schema type every grammar
 * shares (`@lokascript/intent`, through framework), with hyperscript's role and
 * value types. Its field docs say which grammar's code reads each field.
 */
export type RoleSpec = GrammarRoleSpec<SemanticRole, SemanticValue>;

/**
 * How a command's semantic roles map onto its AST shape.
 *
 * Declarative replacement for the hand-written mappers in
 * `ast-builder/command-mappers.ts`. A command that can state its AST shape as
 * data needs no mapper function at all: `buildFromAstShape` reads this and
 * produces byte-identical output.
 *
 * A role entry is either a single role or a **first-present-of chain** — the
 * declarative form of the `destination ?? patient` fallbacks 14 of the original
 * mappers used. The first role in the chain that the parse actually produced
 * wins; the rest are ignored.
 *
 * IMPORTANT: the roles named here are the roles the AST BUILDER reads, which is
 * NOT always the set this schema's `roles` array declares. The parser relabels
 * and reclaims roles after matching (`normalizeCommandRoles`, the event-name
 * reclaim in `buildEventHandler`), so a mapper legitimately reads roles no
 * pattern binds directly. Never "reconcile" the two by editing `roles` — that
 * changes pattern generation for 24 languages. See
 * `ast-shape-consistency.test.ts`, which pins every divergence with a reason.
 */
export interface AstShape {
  /**
   * Roles routed to positional `args`, in emission order. Absent roles are
   * skipped (they do not leave a hole), matching the original mappers'
   * `if (x) args.push(x)`.
   */
  readonly args?: ReadonlyArray<SemanticRole | ReadonlyArray<SemanticRole>>;
  /**
   * Modifier key → role (or first-present-of chain). The key is the AST
   * contract the runtime command reads — usually the English preposition for
   * that role, sometimes a non-preposition contract key (`detail`, `body`).
   * Authoring order is preserved in the emitted object.
   */
  readonly modifiers?: Readonly<Record<string, SemanticRole | ReadonlyArray<SemanticRole>>>;
  /** Emit `isBlocking: true` on the command node (wait / fetch / settle). */
  readonly isBlocking?: boolean;
}

/**
 * A precondition that must be met before command execution.
 * Used for runtime error documentation.
 */
export interface CommandPrecondition {
  /** Human-readable condition description */
  readonly condition: string;
  /** Error code thrown when precondition fails */
  readonly errorCode: string;
  /** Error message template */
  readonly message: string;
}

/**
 * A command schema defines the semantic structure of a command: the shared
 * schema type (`@lokascript/intent`) with hyperscript's action, role and
 * category names, plus the fields only hyperscript's AST builder and its error
 * docs read.
 */
export interface CommandSchema extends GrammarCommandSchema<
  ActionType,
  SemanticRole,
  SemanticValue,
  CommandCategory
> {
  /**
   * Declarative semantic-roles → AST mapping. When present, the AST builder
   * uses it instead of a hand-written mapper. Absent means this action either
   * still has a hand-written mapper (the four with real branching logic:
   * `wait`, `put`, `go`, `pick`) or falls through to
   * `ASTBuilder.buildGenericCommand`'s blanket role mapping.
   */
  readonly ast?: AstShape;

  // Runtime error documentation (optional for backward compatibility)

  /** Possible runtime error codes this command can throw */
  readonly errorCodes?: readonly string[];
  /** Preconditions that must be met before execution */
  readonly preconditions?: readonly CommandPrecondition[];
  /** Recovery hints mapping error code to suggestion */
  readonly recoveryHints?: Readonly<Record<string, string>>;
}

/**
 * A compile-time check, used nowhere: every hyperscript schema is also a domain
 * DSL's schema as it stands (role and action names as plain strings, intent's
 * value type), so the grammar-neutral generator, matcher and renderer take
 * semantic's schemas without a cast. A field whose type here outgrows the shared
 * one fails this line.
 */
export type HyperscriptSchemaIsGrammarNeutral = AssertAssignable<
  CommandSchema,
  GrammarCommandSchema
>;
type AssertAssignable<T extends U, U> = T;

// The schema without its variant-only roles: framework's, with the pattern
// generator it serves (domains rethink Phase 4.4).
export { withoutVariantOnlyRoles } from '@lokascript/framework';

/**
 * Extraction entry for the optional destination slot of event-handler
 * wrapper patterns. Defers to the wrapped command's schema: commands whose
 * schema has no destination role (show/hide/increment/decrement/…) get NO
 * destination extraction, and commands that do declare one inherit the
 * schema's own default. A hardcoded `default: me` here used to fabricate a
 * destination role on every wrapped parse — buildAST prefers destination
 * over patient for the args slot, so the runtime acted on `me` instead of
 * the patient selector (invisible to recall-based R1, caught by R2).
 */
export function eventHandlerDestinationExtraction(
  schema: CommandSchema
): Record<string, { fromRole: string; default?: SemanticValue }> {
  const destRole = schema.roles.find(r => r.role === 'destination');
  if (!destRole) return {};
  return {
    destination: destRole.default
      ? { fromRole: 'destination', default: destRole.default }
      : { fromRole: 'destination' },
  };
}

/**
 * Extraction entry for the optional source slot of event-handler wrapper
 * patterns — same contract as eventHandlerDestinationExtraction: no source
 * role in the wrapped schema → no extraction; a declared role inherits the
 * schema's own default (remove's `from X` defaults to me, matching the
 * runtime's behavior when the modifier is absent).
 */
export function eventHandlerSourceExtraction(
  schema: CommandSchema
): Record<string, { fromRole: string; default?: SemanticValue }> {
  const srcRole = schema.roles.find(r => r.role === 'source');
  if (!srcRole) return {};
  return {
    source: srcRole.default
      ? { fromRole: 'source', default: srcRole.default }
      : { fromRole: 'source' },
  };
}

/**
 * Optional source-phrase group token for event-handler wrapper patterns,
 * emitted only when the wrapped schema declares a source role and the
 * profile has a source marker. The marker's position decides token order:
 * prepositions precede the value (es `de .items`), postpositional particles
 * follow it (ja `.items から`). Wrappers previously had NO source slot at
 * all, so `remove X from Y` translations either silently dropped the
 * from-phrase (SVO/VSO) or leaked it past the matched span where the SOV
 * verb-anchoring fallback read the particle as a bogus `from` command.
 */
export function eventHandlerSourceGroup(
  schema: CommandSchema,
  marker: RoleMarker | undefined
): PatternToken[] {
  return eventHandlerRoleGroup(schema, marker, 'source');
}

/**
 * Trailing destination-phrase group for the SOV wrappers, the destination
 * twin of eventHandlerSourceGroup: the grammar transformer emits
 * `add X to Y`'s to-phrase AFTER the verb (`追加 #item に`), but the SOV
 * wrappers' only destination group sits before the patient, so the trailing
 * phrase leaked past the matched span and the verb-anchoring fallback read
 * the に particle as a bogus `into` command (same failure shape as the
 * source-phrase leak).
 */
export function eventHandlerDestinationGroup(
  schema: CommandSchema,
  marker: RoleMarker | undefined
): PatternToken[] {
  return eventHandlerRoleGroup(schema, marker, 'destination');
}

/**
 * The same optional trailing group, binding `patient`.
 *
 * Only `swap` needs it: it is the two-object command, and its trailing element
 * is the CONTENT (`swap #a with #b` → `#a` takes `#b`'s content). See the
 * swap-gated branches in the SOV/VSO event-handler generators.
 */
export function eventHandlerPatientGroup(
  schema: CommandSchema,
  marker: RoleMarker | undefined
): PatternToken[] {
  return eventHandlerRoleGroup(schema, marker, 'patient');
}

function eventHandlerRoleGroup(
  schema: CommandSchema,
  marker: RoleMarker | undefined,
  role: SemanticRole
): PatternToken[] {
  if (!marker || !schema.roles.some(r => r.role === role)) return [];
  const markerToken: PatternToken = marker.alternatives
    ? { type: 'literal', value: marker.primary, alternatives: [...marker.alternatives] }
    : { type: 'literal', value: marker.primary };
  const roleToken: PatternToken = { type: 'role', role, optional: true };
  return [
    {
      type: 'group',
      optional: true,
      tokens: marker.position === 'after' ? [roleToken, markerToken] : [markerToken, roleToken],
    },
  ];
}

/**
 * Command categories for organization.
 */
export type CommandCategory =
  | 'dom-class' // Class/attribute manipulation
  | 'dom-content' // Content manipulation
  | 'dom-visibility' // Show/hide
  | 'variable' // Variable operations
  | 'event' // Event handling
  | 'async' // Async operations
  | 'navigation' // URL/navigation
  | 'control-flow'; // Control flow

// =============================================================================
// Command Schema Definitions
// =============================================================================

/**
 * Toggle command: adds class/attribute if absent, removes if present.
 *
 * Patterns:
 * - EN: toggle .active on #button
 * - JA: #button の .active を 切り替え
 * - AR: بدّل .active على #button
 */
export const toggleSchema: CommandSchema = {
  action: 'toggle',
  description: 'Toggle a class or attribute on/off',
  category: 'dom-class',
  primaryRole: 'patient',
  // `toggle .active on #btn for 2s` → args ['.active'], modifiers { on, for }.
  ast: { args: ['patient'], modifiers: { on: 'destination', between: 'goal', for: 'duration' } },
  // Role markers the traditional parser leaves in `args` as bare identifiers.
  // Skipped when schema-driven inference scans args for role VALUES, so the
  // marker cannot be bound as one (interchange `roles` only — the runtime
  // reads `raw.args` and is unaffected).
  // `toggle .active on #panel` → args [.active, on, #panel]. This is the
  // row that surfaced the defect (Arc 1 step 4): `destination` bound to the
  // literal identifier 'on' and `#panel` bound to nothing. Semantic binds
  // patient='.active', destination='me'/'#panel'.
  argSkipTokens: ['on'],
  // `toggle my *display` (upstream's form: a style property of an element) is a
  // possessive, which reads as a property-path, not the patient's selector. en and
  // es read it through handcrafted patterns that leave the patient untyped; every
  // generated pattern refused it, so ar/de/fr/he/ja/tr could not read their own
  // render. A lower-priority variant, so it never takes a span a selector matches.
  widenTypeVariants: [
    { role: 'patient', types: ['selector', 'property-path'], idSuffix: 'possessive-patient' },
  ],
  requiredRoleVariants: [
    // `toggle between .a and .b [on #x]`
    { role: 'goal', omit: ['patient'], idSuffix: 'between-pair' },
    // `toggle $x between 1, 2 and 3`, `toggle *display of me between …`
    {
      role: 'goal',
      widen: { role: 'patient', types: ['selector', 'reference', 'expression'] },
      idSuffix: 'between-values',
    },
  ],
  roles: [
    {
      role: 'patient',
      description: 'The class or attribute to toggle',
      required: true,
      expectedTypes: ['selector'],
      selectorKinds: ['class', 'attribute'],
      svoPosition: 1,
      sovPosition: 2,
      // zh: the transformer emits `切换 把 .active` (the profile's 把 patient
      // marker), but natural zh writes `切换 .active` bare — the handcrafted
      // patterns' form. Optional accepts both; without it the generated
      // pattern REQUIRED 把 and the slim bundles could not parse the bare
      // form at all. Same treatment as go's destination.
      markerOptional: { zh: true },
    },
    {
      role: 'destination',
      description: 'The target element (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 1,
      // toggle's destination is LOCATIVE ("toggle .active on #button" — at
      // the element), so ko 에서 ("at") is a legitimate marker HERE. It is no
      // longer a profile-wide destination alternative: for allative
      // destinations (add's "to me") 에서 reads as source, and it let an
      // unconsumed wait-line tail (`문서 에서`) satisfy the next SOV clause's
      // destination group (behavior-draggable). Per-command merge via the
      // #588 markerVariants machinery.
      markerVariants: { ko: ['에서'] },
    },
    {
      role: 'goal',
      description: 'What it toggles between (`between .a and .b`, `between 1, 2 and 3`)',
      required: false,
      expectedTypes: ['expression', 'literal', 'selector'],
      valueShape: 'list',
      svoPosition: 1.5,
      sovPosition: 2.5,
      markerOverride: {
        en: 'between',
        ar: 'بين',
        bn: 'মধ্যে',
        de: 'zwischen',
        es: 'entre',
        fr: 'entre',
        he: 'בין',
        hi: 'के बीच',
        id: 'antara',
        it: 'tra',
        ja: 'の間で',
        ko: '사이',
        ms: 'antara',
        pl: 'między',
        pt: 'entre',
        qu: 'chawpipi',
        ru: 'между',
        sw: 'kati',
        th: 'ระหว่าง',
        tl: 'sa_pagitan',
        tr: 'arasında',
        uk: 'між',
        vi: 'giữa',
        zh: '之间',
      },
      markerPositionOverride: { zh: 'after' },
    },
    {
      role: 'duration',
      description: 'How long the class stays on before reverting (`toggle .a for 2s`)',
      required: false,
      expectedTypes: ['literal'],
      svoPosition: 3,
      sovPosition: 3,
      // `valueShape: 'time'` is what makes the marker-less slot safe in the 21
      // languages whose stored corpus rows carry the duration as an UNMARKED
      // trailing token (the i18n transformer renders `alternar .loading 2s`).
      // Without it this exact role, `required: false`, silently cost es/pl/vi
      // the second toggle's positional destination on `toggle-aria-expanded` —
      // the regression that motivated the R1 role-set flip ratchet.
      valueShape: 'time',
      // en marks with `for`; ja/ko carry real particles because their SOV
      // generators otherwise emit a marker-less optional slot MID-pattern
      // (the R3 family-F1 hazard), which stopped plain
      // `#button の .active を 切り替え` parsing — 16 test failures, measured
      // twice. The other 21 languages deliberately have NO marker: their
      // corpus ground truth is the unmarked trailing form, and the shape
      // constraint is the anchor. Do NOT reason from transitionSchema here —
      // its en-only duration marker survives because a required `to {goal}`
      // phrase sits between patient and duration; toggle has no such anchor.
      markerOverride: { en: 'for', ja: '間', ko: '동안' },
    },
  ],
  // Runtime error documentation
  errorCodes: ['MISSING_ARGUMENT', 'NO_VALID_CLASS_NAMES', 'INVALID_CSS_PROPERTY'],
  preconditions: [
    {
      condition: 'Command has at least one argument',
      errorCode: 'MISSING_ARGUMENT',
      message: 'toggle command requires an argument',
    },
    {
      condition: 'Class names are valid CSS identifiers',
      errorCode: 'NO_VALID_CLASS_NAMES',
      message: 'toggle command: no valid class names found',
    },
  ],
  recoveryHints: {
    MISSING_ARGUMENT: 'Add a class selector (.classname) or attribute to toggle',
    NO_VALID_CLASS_NAMES: 'Ensure class names start with a dot (.) and are valid CSS identifiers',
    INVALID_CSS_PROPERTY: 'Check CSS property name syntax (use kebab-case)',
  },
};

/**
 * `… when <condition>` on add, remove, hide and show: which of the targets the
 * command applies to, tested per element; upstream gives the ones that fail the
 * test the opposite (`add .x to .item when it matches .y` removes `.x` from the
 * rest). The marker is each language's own word for `when`, its profile's
 * `keywords.when` (when-condition.test.ts holds the two together), so this
 * needs no new vocabulary. The schemas had no such role, so since M1 every
 * translation kept the clause in English and a bare command was refused.
 *
 * First in a verb-final language (ja `それ 一致する .doh とき .bar に .rey を
 * 追加`), last everywhere else, as upstream writes it.
 */
function whenConditionRole(svoPosition: number): RoleSpec {
  return {
    role: 'condition',
    description: 'Which targets it applies to (`when it matches .x`); the rest get the opposite',
    required: false,
    expectedTypes: ['expression'],
    valueShape: 'clause',
    svoPosition,
    sovPosition: 0,
    markerOverride: {
      en: 'when',
      es: 'cuando',
      ja: 'とき',
      ar: 'عندما',
      ko: '때',
      zh: '何时',
      tr: 'iken',
      pt: 'quando',
      fr: 'quand',
      de: 'wenn',
      id: 'ketika',
      qu: 'maykama',
      sw: 'wakati',
      bn: 'যখন',
      hi: 'जब',
      it: 'quando',
      ms: 'bila',
      pl: 'kiedy',
      ru: 'когда',
      th: 'ขณะที่',
      tl: 'tuwing',
      uk: 'коли',
      vi: 'lúc',
      he: 'כאשר',
    },
    // The profile's other spellings of `when`.
    markerVariants: {
      ja: ['ときに'],
      tr: ['durumunda', 'olduğunda'],
      he: ['כש'],
    },
  };
}

/**
 * Add command: adds a class or attribute.
 */
export const addSchema: CommandSchema = {
  action: 'add',
  description: 'Add a class or attribute to an element',
  category: 'dom-class',
  primaryRole: 'patient',
  // `add .active to #button` → { args: ['.active'], modifiers: { to: '#button' } }
  ast: { args: ['patient'], modifiers: { to: 'destination', when: 'condition' } },
  roles: [
    {
      role: 'patient',
      description: 'The class or attribute to add',
      required: true,
      // `literal` admits quoted HTML/text content (`add "<p>Line</p>" to me`,
      // repeat-times + behavior-draggable): the selector-only slot rejected the
      // string and the WHOLE command parsed NULL in en and the generated-path
      // languages, while 11 lax-path languages captured patient=literal — and
      // were precision-flagged as "spurious add" against the empty en
      // reference (add ×22, en-noise). Same admission precedent as
      // transition.patient / append.patient.
      expectedTypes: ['selector', 'literal'],
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'destination',
      description: 'The target element (defaults to me)',
      required: false,
      // `expression` admits a loop binding VARIABLE as the destination
      // (`repeat for item in .items add .processed to item`): the bare
      // identifier tokenizes as expression, so [selector, reference] rejected
      // the marked `to item` phrase and the destination silently defaulted to
      // `me` — in the en reference AND most translations alike (it/qu already
      // captured it; everyone else vacuously "matched" the shared noise).
      // Marker-guarded, so an unmarked trailing identifier is not affected.
      expectedTypes: ['selector', 'reference', 'expression'],
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 1,
      // `add` is directional, but every profile's `destination` marker is
      // LOCATIVE (en on, es en, ar على, zh 在, fr sur, de auf, pt em) because
      // it also serves `toggle`/`show`. Without a per-language override the
      // rendered text said "add .class ON #element" in every language but
      // English — the gap lokascript-learn corrects with 6 of its 16 override
      // entries. ja に / ko 에 / tr e are already directional, so they keep
      // the profile default.
      //
      // Tier B (2.9): he/id/it/sw were the remaining locatives that this
      // language actually distinguishes.
      //   he — `על` is "ON"; Hebrew adds with the allative `אל` (`ל` is a bound
      //        prefix, so it cannot stand as a separate marker token).
      //   id — `pada` is "at/on"; `ke` is the directional, and it is what the
      //        i18n corpus already renders for every id destination.
      //   it — `in` is locative; Italian adds with `a` (`aggiungere a`).
      //   sw — `kwenye` is not merely locative, it is sw's EVENT keyword
      //        (`on: 'kwenye'` in the dictionary), so reusing it as a
      //        destination marker collides. `kwa` is the corpus rendering.
      // hi `में` / ru+uk `в` / th `ใน` / vi `vào` are already the right
      // container-directional for "add to", and keep the profile default.
      markerOverride: {
        en: 'to',
        es: 'a',
        ar: 'إلى',
        zh: '到',
        fr: 'à',
        de: 'zu',
        pt: 'a',
        he: 'אל',
        id: 'ke',
        it: 'a',
        sw: 'kwa',
      },
      // Each language's previous primary marker (and its alternates) still
      // parses, so source written against ≤2.8 keeps working.
      markerLegacy: {
        es: ['en', 'sobre', 'hacia'],
        ar: ['على', 'في', 'ب'],
        zh: ['在', '于'],
        fr: ['sur', 'dans'],
        de: ['auf', 'in'],
        pt: ['em', 'para'],
        he: ['על', 'ב', 'ל'],
        id: ['pada', 'di'],
        it: ['in', 'su'],
        sw: ['kwenye'],
      },
    },
    whenConditionRole(3),
  ],
  // Runtime error documentation
  errorCodes: ['MISSING_ARGUMENT', 'NO_VALID_CLASS_NAMES', 'PROPERTY_REQUIRES_VALUE'],
  preconditions: [
    {
      condition: 'Command has at least one argument',
      errorCode: 'MISSING_ARGUMENT',
      message: 'add command requires an argument',
    },
  ],
  recoveryHints: {
    MISSING_ARGUMENT: 'Add a class selector (.classname) or attribute to add',
    NO_VALID_CLASS_NAMES: 'Ensure class names start with a dot (.) and are valid CSS identifiers',
    PROPERTY_REQUIRES_VALUE: 'When adding a property (*prop), provide a value argument',
  },
};

/**
 * Remove command: removes a class or attribute.
 */
export const removeSchema: CommandSchema = {
  action: 'remove',
  description: 'Remove a class or attribute from an element',
  category: 'dom-class',
  primaryRole: 'patient',
  // `remove .active from #btn` → args ['.active'], modifiers { from: '#btn' }.
  ast: { args: ['patient'], modifiers: { from: 'source', when: 'condition' } },
  // Trailing `from X` is captured by the core parser as `target`; map it to source.
  targetRole: 'source',
  // Role markers the traditional parser leaves in `args` as bare identifiers.
  // Skipped when schema-driven inference scans args for role VALUES, so the
  // marker cannot be bound as one (interchange `roles` only — the runtime
  // reads `raw.args` and is unaffected).
  // `remove .active from me` → args [.active, from, me]. `source` has no
  // markerOverride, so positional binding took the marker. Semantic binds
  // patient='.active', source='me'.
  argSkipTokens: ['from'],
  roles: [
    {
      role: 'patient',
      // `remove .class from #el` / `remove @attr from #el` removes a class/attr;
      // `remove me` / `remove it` / `remove #el` removes the ELEMENT itself, where
      // the patient is a reference/selector. Accept references so remove-self (a
      // core behavior pattern) parses instead of throwing.
      description: 'The class/attribute to remove, or the element to remove',
      required: true,
      // A literal too: upstream removes a value from a collection (`remove 3
      // from :arr`) and a style block's properties (`remove {color} from me`,
      // the brace literal fold).
      expectedTypes: [...ELEMENT_TARGET_TYPES, 'literal'],
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'source',
      // 'expression' admits a bare local-variable read (`remove .done from item`
      // — `item` bound by an earlier set). Without it the slot silently rejected
      // the identifier and the `me` default filled in, so the en reference
      // under-captured its own source and every translation that DID capture
      // `item` was penalized against it (behavior-sortable remove.source ×12).
      description: 'The element to remove from (defaults to me)',
      required: false,
      expectedTypes: ['selector', 'reference', 'expression'],
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 1,
    },
    whenConditionRole(3),
  ],
  // Runtime error documentation
  errorCodes: ['MISSING_ARGUMENT', 'NO_VALID_CLASS_NAMES'],
  preconditions: [
    {
      condition: 'Command has at least one argument',
      errorCode: 'MISSING_ARGUMENT',
      message: 'remove command requires an argument',
    },
  ],
  recoveryHints: {
    MISSING_ARGUMENT: 'Add a class selector (.classname) or attribute to remove',
    NO_VALID_CLASS_NAMES: 'Ensure class names start with a dot (.) and are valid CSS identifiers',
  },
};

/**
 * Put command: puts content into a target.
 *
 * Patterns:
 * - EN: put "hello" into #output
 * - JA: #output に "hello" を 置く
 * - AR: ضع "hello" في #output
 */
export const putSchema: CommandSchema = {
  action: 'put',
  description: 'Put content into a target element or variable',
  category: 'dom-content',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The content to put',
      required: true,
      expectedTypes: PUT_VALUE_TYPES,
      svoPosition: 1,
      sovPosition: 1, // SOV: patient comes first (を marker)
    },
    {
      role: 'destination',
      description: 'Where to put the content',
      required: true,
      // A variable too: see PUT_DESTINATION_TYPES.
      expectedTypes: PUT_DESTINATION_TYPES,
      svoPosition: 2,
      sovPosition: 2, // SOV: destination comes second (に/에/a marker)
      // "put 'hello' into #output" — directional, so the same locative-default
      // correction as `add`. es `en` and pt `em` are already right for "into",
      // as are ja に / ko 에 / tr e; only ar/zh/fr/de need an override.
      //
      // Tier B (2.9): `put` is ILLATIVE, so it diverges from `add` where the
      // two senses differ. he takes `ב` ("in/into" — `שים ב`), NOT the allative
      // `אל` that `add`/`go` take. it keeps its locative `in` (`mettere in`) —
      // it is `add`/`go` that needed `a`. id/sw change for the same reason as
      // `add` (directional / event-keyword collision). hi `में`, ru+uk `в`,
      // th `ใน` and vi `vào` are all already the illative.
      markerOverride: {
        en: 'into',
        ar: 'في',
        zh: '到',
        fr: 'dans',
        de: 'in',
        he: 'ב',
        id: 'ke',
        sw: 'kwa',
      },
      // `before` / `after` are alternate position markers; the matched marker
      // is recorded as a literal in the `method` role (a derived role with no
      // surface form of its own — populated by schema-driven role inference).
      markerVariants: { en: ['before', 'after'] },
      markerLegacy: {
        ar: ['على', 'إلى', 'ب'],
        zh: ['在', '于'],
        fr: ['sur', 'à'],
        de: ['auf', 'zu'],
        he: ['על', 'אל', 'ל'],
        id: ['pada', 'di'],
        sw: ['kwenye'],
      },
      methodCarrier: 'method',
    },
  ],
  // Runtime error documentation
  errorCodes: [
    'MISSING_ARGUMENTS',
    'MISSING_CONTENT',
    'MISSING_POSITION',
    'INVALID_POSITION',
    'NO_TARGET',
    'NO_ELEMENTS',
  ],
  preconditions: [
    {
      condition: 'Command has content and position arguments',
      errorCode: 'MISSING_ARGUMENTS',
      message: 'put requires arguments',
    },
    {
      condition: 'Content to put is specified',
      errorCode: 'MISSING_CONTENT',
      message: 'put requires content',
    },
    {
      condition: 'Position keyword is specified (into, before, after, etc.)',
      errorCode: 'MISSING_POSITION',
      message: 'put requires position keyword',
    },
  ],
  recoveryHints: {
    MISSING_ARGUMENTS: 'Use syntax: put <content> into/before/after <target>',
    MISSING_CONTENT: 'Add content to put (string, element, or expression)',
    MISSING_POSITION: 'Add position keyword: into, before, after, at start of, at end of',
    INVALID_POSITION: 'Valid positions: into, before, after, at start of, at end of',
    NO_TARGET: 'Ensure target element exists or use "me" reference',
    NO_ELEMENTS: 'Check that the selector matches existing elements',
  },
};

/**
 * Set command: sets a property or variable.
 *
 * Patterns:
 * - EN: set :count to 10
 * - ES: establecer :count a 10
 * - JA: :count を 10 に 設定
 * - KO: :x 에 5 을 설정 (uses default markers)
 * - TR: :x e 5 i ayarla (uses default markers)
 *
 * Note: Only override markers for SVO languages where patient has no default marker.
 * SOV languages (Korean, Japanese, Turkish) already have correct object markers.
 */
export const setSchema: CommandSchema = {
  action: 'set',
  description: 'Set a property or variable to a value',
  category: 'variable',
  primaryRole: 'destination',
  // Inverted vs most commands: the DESTINATION (the property path being
  // written) is the positional arg and the patient (the value) is the `to`
  // modifier. `scope` carries `set @attr to V on <scope>`, which the core
  // SetCommand applies to every matched element (defaulting to `me`).
  ast: { args: ['destination'], modifiers: { to: 'patient', on: 'scope' } },
  // Role markers the traditional parser leaves in `args` as bare identifiers.
  // Skipped when schema-driven inference scans args for role VALUES, so the
  // marker cannot be bound as one (interchange `roles` only — the runtime
  // reads `raw.args` and is unaffected).
  // `set myVar to "value"` → args [myVar, to, value]; the traditional
  // parser also desugars `increment counter` into the same shape. Semantic
  // binds destination='myVar', patient='value'.
  argSkipTokens: ['to'],
  requiredRoleVariants: [
    // `set {bar: 2, baz: 3} on obj`: an object literal's properties set on a
    // target, with no `to` (upstream's set command). The target may be a
    // variable, which the `set @a … on X` scope never is.
    {
      role: 'scope',
      omit: ['patient'],
      widen: { role: 'scope', types: ['selector', 'reference', 'expression'] },
      idSuffix: 'object-on',
    },
  ],
  roles: [
    {
      role: 'destination',
      description: 'The property or variable to set',
      required: true,
      // 'property-path' opts this role into the "of"-possessive matcher
      // (`set *--x of #y to z`); see pattern-matcher tryMatchOfPossessiveExpression.
      expectedTypes: SET_DESTINATION_TYPES,
      svoPosition: 1,
      sovPosition: 1,
      // Override destination marker for English (remove 'on', use no marker)
      // SOV languages swap markers: variable gets patient marker (を/를/i)
      // Arabic (VSO): no marker before variable
      markerOverride: {
        en: '', // No marker before destination in English: "set :x to 5"
        es: '', // "establecer x a 5" - no marker before variable (profile default `en` matched nothing)
        ja: 'を', // "x を 10 に 設定" - variable gets object marker
        ko: '를', // "x 를 10 에 설정" - variable gets object marker
        tr: 'i', // "x i 10 e ayarla" - variable gets accusative marker
        ar: '', // "عيّن x إلى 10" - no marker before variable
        sw: '', // "seti x kwenye 10" - no marker before variable
        tl: '', // "itakda x sa 10" - no marker before variable
        // th: like every other SVO profile here, the variable is UNMARKED and
        // the value takes the destination preposition (see the patient override
        // below). Absent from both maps, th fell to its profile defaults —
        // destination `ใน`, patient `''` — which put the marker on the wrong
        // operand and left the value bare: `ตั้ง ใน @disabled จริง` for `set
        // @disabled to true`, against the transformer's `ตั้ง @disabled ใน จริง`.
        // Every th `set` row in the corpus carried that inversion; the one it
        // actually broke is set-color-variable, whose property-path destination
        // then swallowed `ของ` as the value inside a handler.
        th: '',
        bn: 'কে', // "x কে 10 তে সেট" - patient marker on variable
        qu: 'ta', // "x ta 10 man churay" - patient marker on variable
        // hi: the transformer marks the TARGET with को and the VALUE with में
        // (`मेरा.textContent को … सेट "Done!" में`) — inverted from the hi profile
        // defaults (destination=में, patient=को). Without these overrides the
        // generated hi set patterns carried the swapped markers and matched no
        // corpus, so the whole hi set-family (set-text/inner-html/style/attribute)
        // fell to the bare-event fallback (S6 set-trio).
        hi: 'को', // target (destination) gets को
        // zh: the transformer emits the verb-first BA form `设置 把 X 到 V`
        // (把 before the variable), while natural zh writes `设置 X 为 V`
        // (unmarked). 把 as the marker, optional below, accepts both. The
        // profile default 在 matched neither form, so the generated zh set
        // patterns were dead weight and the slim bundles (schema-generated
        // patterns only) could not parse set at all.
        zh: '把',
      },
      // Accept the unmarked natural form alongside the transformer's 把 form
      // (same treatment as go's destination).
      markerOptional: { zh: true },
    },
    {
      role: 'patient',
      description: 'The value to set',
      required: true,
      // A variable takes any value: see SET_VALUE_TYPES.
      expectedTypes: SET_VALUE_TYPES,
      svoPosition: 2,
      sovPosition: 2,
      // Override patient marker for SVO languages with their native prepositions
      // SOV languages swap markers: value gets destination marker (に/으로/e)
      // Arabic (VSO): إلى preposition before value
      markerOverride: {
        en: 'to', // "set :x to 5"
        es: 'a', // "establecer x a 10"
        pt: 'para', // "definir x para 10"
        fr: 'à', // "définir x à 10"
        de: 'auf', // "setze x auf 10"
        id: 'ke', // "atur x ke 10"
        ja: 'に', // "x を 10 に 設定" - value gets destination marker
        // ko dict translates set's `to` as 에 (not the manner marker 으로) —
        // every ko corpus emission is `… 설정 {value} 에`; keep aligned.
        ko: '에', // "x 를 10 에 설정" - value gets destination marker
        tr: 'e', // "x i 10 e ayarla" - value gets dative marker
        ar: 'إلى', // "عيّن x إلى 10" - value gets preposition "to"
        sw: 'kwenye', // "seti x kwenye 10" - destination prep before value
        tl: 'sa', // "itakda x sa 10" - destination prep before value
        th: 'ใน', // "ตั้ง x ใน 10" - th's own destination preposition, on the value
        bn: 'তে', // "x কে 10 তে সেট" - destination marker on value
        qu: 'man', // "x ta 10 man churay" - destination marker on value
        hi: 'में', // value (patient) gets में — see the destination note above
        // zh: the transformer/corpus form marks the value with 到 (`设置 把 X
        // 到 V`); natural zh uses 为 (`设置 X 为 V`). 到 is the override,
        // the 为-family rides along as variants (all merged as alternatives
        // by schemaMarkerAlternatives). Profile default 把 was the PATIENT
        // marker, which never precedes set's value in either form.
        zh: '到',
      },
      // Turkish dative is allomorphic under vowel harmony: the i18n transformer
      // emits `e` for quoted-string values (`"red" e`) but `-ya` for a value
      // ending in a vowel (`doğru ya` = "true" in set-attribute). markerOverride
      // is a single string, so the generated tr set patterns carried only `e`
      // and set-attribute fell to the role-scrambling generic SOV extraction.
      // markerVariants supplies the allomorphs, merged in as marker alternatives.
      // Until 2026-07-25 only the SOV two-role generators merged them, so this
      // worked ONLY inside an event handler: `@disabled i doğru ya ayarla` did
      // not parse as a bare command while `tıklama da @disabled i doğru ya
      // ayarla` did. See STRUCTURAL_ARCS_ROADMAP.md (tr set-attribute).
      markerVariants: {
        tr: ['e', 'a', 'ye', 'ya'],
        // Natural zh value markers (`设置 X 为 V`), simplified + traditional +
        // resultative — the handcrafted set-zh-full alternatives, kept parseable
        // alongside the transformer's 到 override above.
        zh: ['为', '為', '成'],
      },
    },
    {
      // The element(s) the attribute/property is set ON — `set @aria-selected to
      // "false" on .tab`. Distinct from `destination` (the attribute) so it does
      // not collide with the value. Optional: a scope-less `set` defaults to `me`
      // (handled in the core runtime). The marker is the literal `on` in every
      // language (passthrough-alignment): the i18n transformer keeps `on <scope>`
      // verbatim, so the same marker captures it back across all languages.
      // See STRUCTURAL_ARCS_ROADMAP.md (S1 tabs-aria) and CORRECTNESS §7bb.
      role: 'scope',
      description: 'The element(s) the attribute/property is set on (defaults to me)',
      required: false,
      expectedTypes: SET_SCOPE_TYPES,
      svoPosition: 3,
      sovPosition: 3,
      markerOverride: {
        en: 'on',
        es: 'on',
        pt: 'on',
        fr: 'on',
        de: 'on',
        it: 'on',
        id: 'on',
        ms: 'on',
        sw: 'on',
        tl: 'on',
        zh: 'on',
        vi: 'on',
        ru: 'on',
        uk: 'on',
        pl: 'on',
        th: 'on',
        he: 'on',
        ar: 'on',
        ja: 'on',
        ko: 'on',
        tr: 'on',
        hi: 'on',
        bn: 'on',
        qu: 'on',
      },
    },
  ],
  // Runtime error documentation
  errorCodes: ['MISSING_TARGET', 'INVALID_TARGET', 'MISSING_VALUE', 'INVALID_SYNTAX'],
  preconditions: [
    {
      condition: 'Command has a target variable or property',
      errorCode: 'MISSING_TARGET',
      message: 'set command requires a target',
    },
    {
      condition: 'Target is a valid variable or property reference',
      errorCode: 'INVALID_TARGET',
      message: 'set command target must be a string or object literal',
    },
    {
      condition: 'Value is specified with "to" keyword',
      errorCode: 'MISSING_VALUE',
      message: 'set command requires a value (use "to" keyword)',
    },
  ],
  recoveryHints: {
    MISSING_TARGET: 'Add a target: set :variable to value OR set element.property to value',
    INVALID_TARGET:
      'Use local variable (:name), element property (el.prop), or "the X of Y" syntax',
    MISSING_VALUE: 'Add "to <value>" to specify what to set',
    INVALID_SYNTAX: 'Use syntax: set <target> to <value>',
  },
};

/**
 * Bind command: reactively binds a variable to a form/element value.
 *
 * Patterns:
 * - EN: bind $greeting to #name-input
 * - ES: vincular $greeting a #name-input
 * - JA: $greeting を #name-input に バインド
 *
 * Structurally mirrors `set` (a destination plus a "to"-marked operand), so it
 * reuses the same per-language marker placement: the bound variable takes the
 * destination slot (object marker in SOV), and the element takes the
 * "to"-preposition slot.
 */
export const bindSchema: CommandSchema = {
  action: 'bind',
  description: 'Reactively bind a variable to an element or property value',
  category: 'variable',
  primaryRole: 'destination',
  roles: [
    {
      role: 'destination',
      description: 'The variable to bind',
      required: true,
      // Either side of a binding can be any writable expression (upstream reads
      // `bind <expr> and|with|to <expr>`): a class (`bind .dark to $isDark`, a
      // class toggle), an attribute, a possessive (`bind my value to …`).
      expectedTypes: ['reference', 'expression', 'selector', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
      // Bound variable mirrors `set`'s destination marking. The bound
      // variable is bare in SVO/VSO/V2 languages (no leading preposition);
      // SOV/agglutinative languages attach an object marker after it. Every
      // supported language is listed explicitly so the generator never falls
      // back to a default destination preposition (which produced a spurious
      // marker, e.g. "vincular en {var}" for Spanish).
      markerOverride: {
        en: '', // "bind $x to #input"
        es: '',
        pt: '',
        fr: '',
        de: '',
        it: '',
        id: '',
        ms: '',
        vi: '',
        ru: '',
        uk: '',
        pl: '',
        th: '',
        zh: '',
        he: '',
        ar: '',
        sw: '',
        tl: '',
        ja: 'を', // variable gets object marker
        ko: '를',
        tr: 'i',
        bn: 'কে',
        qu: 'ta',
        hi: 'को',
      },
    },
    {
      role: 'source',
      description: 'The element or property to bind to',
      required: true,
      // 'property-path' opts this role into the "of"-possessive matcher, so the
      // property-first render of `bind $x to #y's prop` (es `valor de #picker`,
      // ar `قيمة لـ #picker`) keeps its owner selector instead of collapsing to
      // the bare property word; see pattern-matcher tryMatchOfPossessiveExpression.
      // The selector-first languages (en `#picker's value`, ja `#pickerの 値`)
      // already reached property-path through tryMatchPossessiveSelectorExpression.
      expectedTypes: ['selector', 'reference', 'expression', 'property-path'],
      svoPosition: 2,
      sovPosition: 2,
      // Element mirrors `set`/`add`/`put`'s value ("to") marking per language.
      // Each language uses its generic destination preposition (the same one
      // the i18n translator and the other "to"-commands emit) so that
      // auto-generated translations parse — e.g. German "zu", Italian "in".
      markerOverride: {
        en: 'to', // "bind $x to #input"
        es: 'a',
        pt: 'para',
        fr: 'à',
        de: 'zu',
        it: 'in',
        id: 'ke',
        ms: 'ke', // Malay grammar profile now emits the dative `ke` (mirrors id)
        vi: 'vào', // generic destination preposition (matches set/put `into`), not "với"/with
        ru: 'в', // generic destination preposition (matches set/put `into`)
        uk: 'в',
        pl: 'do',
        ja: 'に',
        ko: '에',
        tr: 'e',
        ar: 'إلى',
        zh: '到', // bind target uses 到 (arrive/to), not the default 从/"from"
        he: 'ל', // "to" prefix preposition
        sw: 'kwa', // matches the generic `kwa` the transformer emits (not `kwenye`)
        th: 'ใน', // generic destination preposition the transformer emits
        tl: 'sa',
        bn: 'তে',
        qu: 'man',
        hi: 'में', // SOV locative the transformer emits for the bind target
      },
      // bn's locative is `তে` after a vowel and `এ` after a consonant, one marker:
      // the fused `আমাতে` reads back as `আমি` + `এ` (as the profile's destination
      // takes both), so bind's `আমাতে বাইন্ড` read as nothing.
      markerVariants: { bn: ['এ'] },
    },
  ],
  errorCodes: ['MISSING_TARGET', 'MISSING_VALUE', 'INVALID_SYNTAX'],
  recoveryHints: {
    MISSING_TARGET: 'Add a variable to bind: bind $name to #input',
    MISSING_VALUE: 'Add "to <element>" to specify the binding source',
    INVALID_SYNTAX: 'Use syntax: bind <variable> to <element>',
  },
};

/**
 * Live command: a reactive block whose body re-runs when its tracked
 * dependencies change.
 *
 * Patterns:
 * - EN: live put `Count: ${$count}` into me end
 *
 * `live` takes no leading role — the body immediately follows the keyword —
 * so it opts into `bareKeyword` pattern generation. Like other block commands
 * (`repeat`/`for`), the body is parsed by the surrounding block machinery; the
 * command node itself just carries the `live` action.
 */
export const liveSchema: CommandSchema = {
  action: 'live',
  description: 'Reactive block: re-run the body when its dependencies change',
  category: 'control-flow',
  primaryRole: 'patient',
  hasBody: true,
  bareKeyword: true,
  roles: [],
};

/**
 * When command: the reactive OBSERVER block —
 * `when <expr> [or <expr>]* changes <body> [end]` — whose body re-runs when any
 * watched expression's value changes (`it` is the new value).
 *
 * Verified against the vendored _hyperscript 0.9.93 engine: `or` is the only
 * separator (a comma is rejected — "Expected 'changes' but found ','"),
 * `changes` is a REQUIRED literal, `end` is optional, and the watched expression
 * may be a variable, an `or`-run, arithmetic, a parenthesized expression, a
 * property path or a selector. The engine has NO temporal `when <event>` form
 * (`when click …` fails with "Cannot watch local variable 'click'").
 *
 * Patterns:
 * - EN: when $firstName or $lastName changes put `${$firstName} ${$lastName}` into #full-name end
 * - JA: とき $firstName または $lastName 変わったら `…` を #full-name に 置く 終わり
 *
 * The reactive sibling of `live`, but WITH a head: the watched expression is
 * carried in the `condition` role — the role the `if`/`unless`/`while` heads and
 * the i18n transformer's `BlockStructure.prefixExpr` already use, so the
 * renderer and the R1 role scorer know it. `structuralOnly`: the head is
 * `<when-word> {condition} <changes-word>` with the trailing literal bounding a
 * multi-token capture, which no generated pattern expresses — and the temporal
 * `when {event}` handler patterns must keep winning for `when click …`.
 * `tryParseFeatureBlock` parses the head; the body follows the `live` path.
 */
export const whenSchema: CommandSchema = {
  action: 'when',
  description: 'Reactive observer: re-run the body when a watched expression changes',
  category: 'control-flow',
  primaryRole: 'condition',
  // Bare-command form only — the real path is ASTBuilder.buildFeature.
  ast: { args: ['condition'] },
  hasBody: true,
  structuralOnly: true,
  roles: [
    {
      role: 'condition',
      description: 'The watched expression (`or`-separated expressions watch each)',
      required: true,
      expectedTypes: ['expression', 'reference', 'selector'],
      svoPosition: 1,
    },
  ],
};

/**
 * Realtime / service-worker block commands.
 *
 * Each introduces a named, body-bearing construct whose body is a sequence of
 * statements (often an `on <event> … end` handler), e.g.:
 * - `eventsource ChatStream from /events on message put it into #messages end`
 * - `socket ChatSocket ws://host on message put it into #chat end`
 * - `worker Calculator def add(a, b) return a + b end end`
 * - `intercept / precache /, /app.js as "v1" on /api/* use network-first end`
 *
 * Like `live`, the keyword takes no fixed leading role (the name/source/body
 * vary in shape), so they use `bareKeyword` generation: the parsed node
 * carries the action and the body is handled by the block machinery. Richer
 * role extraction (name, source) can be layered on later if needed.
 */
export const eventsourceSchema: CommandSchema = {
  action: 'eventsource',
  description: 'Open a Server-Sent Events stream and handle its events',
  category: 'async',
  primaryRole: 'patient',
  hasBody: true,
  bareKeyword: true,
  roles: [],
};

export const socketSchema: CommandSchema = {
  action: 'socket',
  description: 'Open a WebSocket and handle its messages',
  category: 'async',
  primaryRole: 'patient',
  hasBody: true,
  bareKeyword: true,
  roles: [],
};

export const workerSchema: CommandSchema = {
  action: 'worker',
  description: 'Define a Web Worker with inline methods',
  category: 'async',
  primaryRole: 'patient',
  hasBody: true,
  bareKeyword: true,
  roles: [],
};

export const interceptSchema: CommandSchema = {
  action: 'intercept',
  description: 'Service-worker fetch interception / caching strategies',
  category: 'async',
  primaryRole: 'patient',
  hasBody: true,
  bareKeyword: true,
  roles: [],
};

/**
 * Show command: makes an element visible.
 */
export const showSchema: CommandSchema = {
  action: 'show',
  description: 'Make an element visible',
  category: 'dom-visibility',
  primaryRole: 'patient',
  // The target arrives as EITHER role depending on which pattern matched, so
  // the arg is a first-present-of chain. NOTE: `duration` and `destination`
  // are read here but not declared in `roles` below — the parser relabels into
  // them. Pinned in ast-shape-consistency.test.ts.
  ast: {
    args: [['destination', 'patient']],
    modifiers: { with: ['style', 'duration'], when: 'condition' },
  },
  roles: [
    {
      role: 'patient',
      description: 'The element to show',
      // Optional: a bare `show` shows `me` (`you` inside a `tell`) on both
      // engines. Required, a bare `show` matched no pattern and was DROPPED in
      // every language — `on click show then log 1` rendered `on click log 1`,
      // and `tell #modal to show` lost its only body command.
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'style',
      // `with <strategy>`: a name (`opacity`, an expression) or a `*`-prefixed
      // style ref (`*opacity`, which tokenizes as a selector). As a literal
      // only, neither was ever captured: `hide me with *opacity` rendered
      // `hide me`.
      description: 'The hide/show strategy (`with opacity`, `with *opacity`)',
      required: false,
      expectedTypes: VISIBILITY_STYLE_TYPES,
      svoPosition: 2,
      sovPosition: 2,
    },
    whenConditionRole(3),
  ],
};

/**
 * Hide command: makes an element invisible.
 */
export const hideSchema: CommandSchema = {
  action: 'hide',
  description: 'Make an element invisible',
  category: 'dom-visibility',
  primaryRole: 'patient',
  // The target arrives as EITHER role depending on which pattern matched.
  // NOTE: `duration`/`destination` are read here but not declared in `roles`.
  ast: {
    args: [['destination', 'patient']],
    modifiers: { with: ['style', 'duration'], when: 'condition' },
  },
  roles: [
    {
      role: 'patient',
      description: 'The element to hide',
      // Optional, as `show`'s: a bare `hide` was dropped in every language.
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'style',
      // `with <strategy>`: a name (`opacity`, an expression) or a `*`-prefixed
      // style ref (`*opacity`, which tokenizes as a selector). As a literal
      // only, neither was ever captured: `hide me with *opacity` rendered
      // `hide me`.
      description: 'The hide/show strategy (`with opacity`, `with *opacity`)',
      required: false,
      expectedTypes: VISIBILITY_STYLE_TYPES,
      svoPosition: 2,
      sovPosition: 2,
    },
    whenConditionRole(3),
  ],
};

/**
 * On command: event handler.
 */
export const onSchema: CommandSchema = {
  action: 'on',
  description: 'Handle an event',
  category: 'event',
  primaryRole: 'event',
  // Event-handler declaration reduced to a bare command node; the real
  // handler path is ASTBuilder.buildEventHandler, not this descriptor.
  ast: { args: ['event'], modifiers: { from: 'source' } },
  hasBody: true,
  roles: [
    {
      role: 'event',
      description: 'The event to handle',
      required: true,
      // `literal` covers the localized event names (a keyword token). A CUSTOM
      // event name passes through untranslated and tokenizes as an identifier —
      // see `widenTypeVariants` below, which carries that case in a separate,
      // lower-priority pattern rather than here.
      expectedTypes: ['literal'],
      svoPosition: 1,
      sovPosition: 2,
      // ja's head is `クリックで` and tr's `tıklamada`, the event and the `on`
      // word (M2 N4): the event's own marker (`を`, `i`) is read, as code
      // written before 4.5.0 has it (`クリック を で`, `tıklama i üzerinde`),
      // and not written.
      markerOptional: { ja: true, tr: true },
    },
    {
      role: 'source',
      description: 'The element to listen on (defaults to me)',
      required: false,
      // `expression`: a variable or behavior parameter (`on click from
      // triggerEl`, behavior-removable) and upstream's `elsewhere`, both typed
      // expression by the en parse. Without it every generated head pattern's
      // `[{source} から]` / `[من {source}]` slot rejected them and the render the
      // handler source now gets (spliceEventModifiers) did not parse back.
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 1,
    },
  ],
  widenTypeVariants: [
    {
      role: 'event',
      types: EVENT_NAME_TYPES,
      idSuffix: 'expr-event',
      excludeLanguages: ['ko'],
    },
  ],
};

/**
 * Trigger command: dispatches an event.
 * Supports namespaced events like "draggable:start".
 */
export const triggerSchema: CommandSchema = {
  action: 'trigger',
  description: 'Trigger/dispatch an event',
  category: 'event',
  primaryRole: 'event',
  // `trigger click on #btn` → args ['click'], modifiers { on: '#btn' }.
  ast: { args: ['event'], modifiers: { on: 'destination' } },
  // Role markers the traditional parser leaves in `args` as bare identifiers.
  // Skipped when schema-driven inference scans args for role VALUES, so the
  // marker cannot be bound as one (interchange `roles` only — the runtime
  // reads `raw.args` and is unaffected).
  // `trigger click on #button` → args [click, on, #button]. Semantic binds
  // event='click', destination='#button'.
  argSkipTokens: ['on'],
  roles: [
    {
      role: 'event',
      description: 'The event to trigger (supports namespaced events like draggable:start)',
      required: true,
      expectedTypes: EVENT_NAME_TYPES, // expression for custom/namespaced event names
      svoPosition: 1,
      sovPosition: 2,
      // hi/qu/bn mark trigger's event ACCUSATIVELY (`draggable:start को ट्रिगर`,
      // `draggable:start ta kichay`, `draggable:start কে ট্রিগার` — the corpus
      // renderings), but their profile-wide event marker is the on-handler one
      // (hi पर, qu locative pi, bn এ), so the generated SOV pattern never
      // matched and the whole line fell through to the on-handler reading (hi)
      // or failed outright (qu/bn). ja/ko were immune only because their event
      // marker IS the object particle (を / 을·를). #588 markerVariants machinery.
      markerVariants: { hi: ['को'], qu: ['ta'], bn: ['কে'] },
      // …and the RENDER side of the same fact: without an override the
      // renderer emitted the profile-wide event marker (bn তে / hi पर), which
      // doubles as the on-HANDLER head marker — `init তে ট্রিগার` read back as
      // `on init` and the trigger's event dropped (trigger-event bn/hi, bare
      // render allowlist). Rendering the accusative form matches the corpus
      // and cannot re-anchor as a handler. Scoped to this slot: the
      // profile-wide marker stays for the `on` command's own patterns.
      markerOverride: { bn: 'কে', hi: 'को' },
    },
    {
      role: 'destination',
      description: 'The target element (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 1,
    },
  ],
};

/**
 * Wait command: pauses execution.
 */
export const waitSchema: CommandSchema = {
  action: 'wait',
  description: 'Wait for a duration or event',
  category: 'async',
  primaryRole: 'duration',
  roles: [
    {
      role: 'duration',
      description: 'Duration or event to wait for',
      required: true,
      expectedTypes: DURATION_TYPES,
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Fetch command: makes HTTP request.
 */
export const fetchSchema: CommandSchema = {
  action: 'fetch',
  description: 'Fetch data from a URL',
  category: 'async',
  primaryRole: 'source',
  // FetchCommand reads its RequestInit out of `with`. The options object owns
  // that slot; the bare `method` role only falls back into it when no options
  // object was given — hence the chain. `body` is a runtime contract key, not
  // a preposition.
  ast: {
    args: ['source'],
    modifiers: { with: ['style', 'method'], as: 'responseType', body: 'patient' },
    isBlocking: true,
  },
  roles: [
    {
      role: 'source',
      description: 'The URL to fetch from',
      required: true,
      expectedTypes: FETCH_SOURCE_TYPES,
      svoPosition: 1,
      sovPosition: 1,
      // No markerOverride — uses profile default 'from' for parsing.
      renderOverride: { en: '' }, // "fetch /api" (rendering — no preposition)
    },
    {
      role: 'style',
      description: 'Request options object (method, headers, body, credentials…)',
      required: false,
      // expression-ONLY: the pattern matcher routes a `{ … }` run in an
      // expression-only slot through its object-literal fold, which preserves the
      // source text so the expression parser can build a real objectLiteral.
      // `style` is the role whose marker is `with` in every language profile.
      expectedTypes: FETCH_OPTIONS_TYPES,
      // Shape-anchored, so an UNCAPTURED style slot carries no evidence against
      // the pattern. This is the same lever toggle's `[{duration}]` and swap's
      // `[using view {manner}]` needed, and for the same reason: giving the 23
      // hand-written fetch recovery patterns a `[with {style}]` group weighted an
      // unfilled slot into every plain fetch's denominator, dropping es
      // `buscar '/x' como json` from confidence 1.0 to 0.692 — under the 0.7 bar
      // at which core keeps the semantic parse, so a correct parse would have
      // been thrown away for the traditional fallback.
      //
      // It qualifies on the documented criterion: the slot is marker-guarded in
      // every pattern (`con {…}`), and expression-only with an object-literal
      // fold, so nothing else in the grammar can land in it and its absence says
      // only that the caller passed no options. `'object'` rather than reusing
      // `'keyword'` because the two matcher branches that read the VALUE
      // (pattern-matcher.ts:632, :984) are keyword-specific; only the confidence
      // model reads shape-anchoring generically, which is exactly the effect
      // wanted here.
      valueShape: 'object',
      svoPosition: 2,
      sovPosition: 2,
    },
    {
      role: 'responseType',
      description: 'Response format (json, text, html, blob, etc.)',
      required: false,
      expectedTypes: FETCH_RESPONSE_TYPES, // json/text/html are identifiers → expression type
      svoPosition: 3,
      sovPosition: 3,
      // `as` in en; `として` ("as/qua") in ja. ja's profile style marker is the
      // instrumental particle `で`, which is ALSO its event marker, so the
      // rendered `modal で #dlg を 開く` reads as an event-handler head and the
      // whole line parsed as `on modal` rather than `open` — a fresh break the
      // 23-language round-trip probe caught, since making the role bind at all
      // is what first put a style phrase in front of a ja verb.
      //
      // ja ONLY: the probe (`translate(en→L)` → `parse(L)`, both variants, all
      // 23 languages) shows every other language already round-trips both
      // `modal` and `non-modal` on its existing marker. ko `로서` and tr
      // `olarak` were tried and reverted — they changed the rendered surface
      // without fixing anything.
      markerOverride: { en: 'as', ja: 'として' }, // "fetch /api as json" — needed by schema-driven role inference
    },
    {
      role: 'method',
      description: 'HTTP method (GET, POST, etc.)',
      required: false,
      expectedTypes: ['literal'],
      svoPosition: 4,
      sovPosition: 4,
    },
    {
      role: 'destination',
      description: 'Where to store the result',
      required: false,
      expectedTypes: ['selector', 'reference'],
      svoPosition: 5,
      sovPosition: 5,
    },
  ],
};

/**
 * Increment command: increases a numeric value.
 */
export const incrementSchema: CommandSchema = {
  action: 'increment',
  description: 'Increment a numeric value',
  category: 'variable',
  primaryRole: 'patient',
  // `increment #count by 5` → args ['#count'], modifiers { by: 5 }.
  // The counter arrives as destination or patient depending on the pattern.
  ast: { args: [['destination', 'patient']], modifiers: { by: 'quantity' } },
  roles: [
    {
      role: 'patient',
      description: 'The value to increment',
      required: true,
      // 'property-path' opts this role into the "of"-possessive matcher
      // (`increment the textContent of #out`), as set's destination does; without
      // it the owner was stranded and the command incremented a bare word.
      expectedTypes: ['selector', 'reference', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'quantity',
      description: 'Amount to increment by (defaults to 1)',
      required: false,
      // Any value, as `set`'s: upstream reads `by` as an expression and
      // parseFloats it. Literal-only, `by n`, `by -2` and `by obj.v` matched
      // nothing, and the whole `by …` clause fell away: the command
      // incremented by 1, in English and so in every translation.
      expectedTypes: ['literal', 'selector', 'expression', 'reference', 'property-path'],
      default: { type: 'literal', value: 1, dataType: 'number' },
      svoPosition: 2,
      sovPosition: 2,
      // "increment :count by 5". The by-marker langs render the amount with a
      // preposition the quantity role must recognize, else it strands and the
      // amount defaults to 1 (surfaced by the R2 sweep on increment-by-amount).
      // Space-marked SVO langs (it/zh/…) render the amount BARE and capture it
      // positionally, so they need no marker.
      markerOverride: { en: 'by', es: 'por', pt: 'por', fr: 'par', de: 'um' },
    },
  ],
};

/**
 * Decrement command: decreases a numeric value.
 */
export const decrementSchema: CommandSchema = {
  action: 'decrement',
  description: 'Decrement a numeric value',
  category: 'variable',
  primaryRole: 'patient',
  // Mirror of increment.
  ast: { args: [['destination', 'patient']], modifiers: { by: 'quantity' } },
  roles: [
    {
      role: 'patient',
      description: 'The value to decrement',
      required: true,
      // 'property-path' opts this role into the "of"-possessive matcher
      // (`decrement the textContent of #out`), as set's destination does; without
      // it the owner was stranded and the command decremented a bare word.
      expectedTypes: ['selector', 'reference', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'quantity',
      description: 'Amount to decrement by (defaults to 1)',
      required: false,
      // Any value, as increment's.
      expectedTypes: ['literal', 'selector', 'expression', 'reference', 'property-path'],
      default: { type: 'literal', value: 1, dataType: 'number' },
      svoPosition: 2,
      sovPosition: 2,
      // "decrement :count by 5" — same by-marker recognition as increment.
      markerOverride: { en: 'by', es: 'por', pt: 'por', fr: 'par', de: 'um' },
    },
  ],
};

/**
 * Append command: appends content to an element.
 */
export const appendSchema: CommandSchema = {
  action: 'append',
  description: 'Append content to an element',
  category: 'dom-content',
  primaryRole: 'patient',
  // `append "text" to #out` → args ['text'], modifiers { to: '#out' }.
  ast: { args: ['patient'], modifiers: { to: 'destination' } },
  // `append <content>` with no destination targets the implicit result (`it`),
  // which core and upstream both support. Modeled as a separate no-destination
  // pattern rather than `required: false`: an optional role becomes a SKIPPABLE
  // marker group, and the bare value then re-binds by particle metadata in
  // marker languages (the verified `transition` regression, see the goal-role
  // note above). It also keeps `requiredRoles.length === 2`, so append's fused
  // SOV/VSO event-handler patterns are untouched in all 24 languages.
  omitRoleVariants: ['destination'],
  roles: [
    {
      role: 'patient',
      description: 'The content to append',
      required: true,
      // `reference` admits `append it to #out` / `append result to #out` —
      // context references tokenize as `reference`, and the single-token check
      // in pattern-matcher compares expectedTypes with a raw `.includes`, so the
      // type has to be listed explicitly. Same list as put.patient.
      expectedTypes: PUT_VALUE_TYPES,
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'destination',
      description: 'The element to append to',
      required: true,
      // `expression` admits a bare-identifier target (`append item to myArray`,
      // `append " World" to greeting`) — both documented core forms, and both
      // rejected while this was [selector, reference], since a plain identifier
      // tokenizes as `expression`. Marker-guarded, same precedent as
      // add.destination (#571) and send.destination.
      expectedTypes: APPEND_DESTINATION_TYPES,
      svoPosition: 2,
      sovPosition: 1,
      markerOverride: { en: 'to' }, // "append <content> to #element"
    },
  ],
};

/**
 * Prepend command: prepends content to an element.
 */
export const prependSchema: CommandSchema = {
  action: 'prepend',
  description: 'Prepend content to an element',
  category: 'dom-content',
  primaryRole: 'patient',
  // `prepend "text" to #out` → args ['text'], modifiers { to: '#out' }.
  ast: { args: ['patient'], modifiers: { to: 'destination' } },
  // Kept in lockstep with appendSchema — see its notes for why each of these is
  // shaped the way it is.
  omitRoleVariants: ['destination'],
  roles: [
    {
      role: 'patient',
      description: 'The content to prepend',
      required: true,
      expectedTypes: PUT_VALUE_TYPES,
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'destination',
      description: 'The element to prepend to',
      required: true,
      expectedTypes: APPEND_DESTINATION_TYPES,
      svoPosition: 2,
      sovPosition: 1,
      markerOverride: { en: 'to' }, // "prepend <content> to #element"
    },
  ],
};

// =============================================================================
// Batch 1 - Simple Commands
// =============================================================================

/**
 * Log command: logs a value to console.
 */
export const logSchema: CommandSchema = {
  action: 'log',
  description: 'Log a value to the console',
  category: 'variable',
  primaryRole: 'patient',
  // `log "hello"` → args ['hello'].
  ast: { args: ['patient'] },
  roles: [
    {
      role: 'patient',
      description: 'The value to log',
      required: true,
      expectedTypes: ['literal', 'selector', 'reference', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Get command: retrieves a value.
 */
export const getCommandSchema: CommandSchema = {
  action: 'get',
  description: 'Get a value from a source',
  category: 'variable',
  primaryRole: 'source',
  // `get #el's value` → args [value]. Source or patient, whichever bound.
  ast: { args: [['source', 'patient']] },
  roles: [
    {
      role: 'source',
      description: 'The source to get from',
      required: true,
      expectedTypes: GET_SOURCE_TYPES,
      svoPosition: 1,
      sovPosition: 2,
      // Marker overrides for GET pattern
      // SOV languages use object markers, SVO languages have no marker
      markerOverride: {
        en: '',
        es: '',
        pt: '',
        fr: '',
        de: '',
        ja: 'を', // Japanese object marker: #element を 取得
        zh: '',
        ko: '를', // Korean object marker: #element 를 가져오기
        ar: 'على', // Arabic preposition: احصل على #element
        tr: 'i', // Turkish accusative: #element i al
        id: '',
        he: 'את', // Hebrew accusative: קבל את #element (the transformer marks get's object with את)
        sw: '', // Swahili SVO: pata #element (no marker)
        tl: '', // Tagalog VSO: kunin #element (no marker)
        bn: 'কে', // Bengali SOV: #element কে পান (patient marker)
        qu: 'ta', // Quechua SOV: #element ta taripay (patient marker)
      },
    },
  ],
};

/**
 * Take command: takes/removes content from a source.
 */
export const takeSchema: CommandSchema = {
  action: 'take',
  description: 'Take content from a source element',
  category: 'dom-content',
  primaryRole: 'patient',
  // `take .active from #parent for me` → args ['.active'], modifiers { from, for }.
  ast: { args: ['patient'], modifiers: { from: 'source', for: 'recipient' } },
  // Role markers the traditional parser leaves in `args` as bare identifiers.
  // Skipped when schema-driven inference scans args for role VALUES, so the
  // marker cannot be bound as one (interchange `roles` only — the runtime
  // reads `raw.args` and is unaffected).
  // `take class from <#source/>` → args [class, from, selector]. Same
  // family as remove; the marker is not the source.
  argSkipTokens: ['from'],
  roles: [
    {
      role: 'patient',
      description: 'The class or element to take',
      required: true,
      expectedTypes: ['selector'],
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'source',
      // NO default. Upstream's bare `take .active` removes the class from
      // every element CURRENTLY HOLDING it, not from `me` — a `me` default
      // here parsed `take .active` as "take from me", which the runtime then
      // faithfully executed as a near-no-op (remove from me, add to me).
      // Absent source is the signal the runtime needs to run the
      // all-current-holders form.
      description: 'The element to take from (defaults to all current holders)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      svoPosition: 2,
      sovPosition: 1,
    },
    {
      role: 'recipient',
      description: 'The element the class moves ONTO (`take .active from .tab for me`)',
      required: false,
      // References only, deliberately. Upstream accepts an element expression,
      // but the 23 non-en corpus surfaces carry the recipient as a BARE
      // trailing pronoun (i18n renders `… von .tab-button ich`), so this slot
      // is marker-less almost everywhere and a selector-typed slot would
      // swallow the second selector of `take .active from .a .b`. English is
      // unaffected: `take` is on the core parser's skipSemanticParsing list,
      // so the traditional parser owns `for <anything>` there.
      expectedTypes: ['reference'],
      svoPosition: 3,
      sovPosition: 3,
      // NO default. The runtime already defaults the recipient to `me`
      // (commands/animation/take.ts), and a schema default would emit an
      // explicit `for me` modifier on every take — re-smuggling the exact
      // "absent means absent" semantics #859 removed from `source`.
      //
      // Shape-anchored on `reference`: without it this optional slot weighs
      // into every take pattern's denominator in `scoreRoleCoverage`, dropping
      // plain `take .active from .tab-button` from 1.0 to ~0.69 — the toggle-es
      // regression class the R1 role-set flip ratchet exists to catch.
      valueShape: 'reference',
      // en only. The other 23 languages render the recipient unmarked, so a
      // marker there would never match their corpus rows; where the marker-less
      // slot proves inert they stay uncaptured and the pattern lands in the
      // baseline's `roleLossyPatterns` as named R1 burn-down tail. A language
      // gets a real particle here ONLY if the empty slot perturbs its other
      // parses (toggle's ja 間 / ko 동안 case).
      markerOverride: { en: 'for' },
    },
  ],
};

/**
 * Make command: creates a new element.
 */
export const makeSchema: CommandSchema = {
  action: 'make',
  description: 'Create a new element',
  category: 'dom-content',
  primaryRole: 'patient',
  // `make a Date` → args [Date].
  ast: { args: ['patient'] },
  roles: [
    {
      role: 'patient',
      description: 'The element type or selector to create',
      required: true,
      expectedTypes: ['literal', 'selector'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Halt command: stops execution or event propagation.
 * Supports: halt, halt the event, halt default, halt the bubbling
 */
export const haltSchema: CommandSchema = {
  action: 'halt',
  description: 'Halt/stop execution or event propagation',
  category: 'control-flow',
  primaryRole: 'patient',
  // The patient distinguishes `halt the event` (preventDefault, handler
  // CONTINUES) from bare `halt` (stops the handler). Dropping it collapsed
  // both to the bare form. HaltCommand resolves a 'the' target to context.event.
  // `halt the event` is the `the` slot on both paths (Arc 3 step 3): the
  // core parser emits `modifiers.the`, and this descriptor makes the
  // semantic path emit the patient there too — HaltCommand resolves the slot
  // to context.event, and core's role inferrer reads this map in reverse.
  ast: { args: [], modifiers: { the: 'patient' } },
  // Role markers the traditional parser used to leave in `args` as bare
  // identifiers; skipped when schema-driven inference scans args for role
  // VALUES. Semantic binds patient='event'.
  argSkipTokens: ['the'],
  roles: [
    {
      role: 'patient',
      description: 'What to halt (event, default, bubbling, etc.)',
      required: false, // Plain "halt" is valid
      expectedTypes: ['literal', 'reference', 'expression'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Settle command: waits for animations to complete.
 */
export const settleSchema: CommandSchema = {
  action: 'settle',
  description: 'Wait for animations/transitions to settle',
  category: 'async',
  primaryRole: 'patient',
  // `settle #el` → args ['#el']; blocking (waits for transitions to finish).
  ast: { args: [['destination', 'patient']], isBlocking: true },
  roles: [
    {
      role: 'patient',
      description: 'The element to settle (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Throw command: throws an error/exception.
 */
export const throwSchema: CommandSchema = {
  action: 'throw',
  description: 'Throw an error',
  category: 'control-flow',
  primaryRole: 'patient',
  // `throw "message"` → args ['message'].
  ast: { args: ['patient'] },
  roles: [
    {
      role: 'patient',
      description: 'The error message or object to throw',
      required: true,
      expectedTypes: ['literal', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Send command: sends/dispatches an event.
 */
export const sendSchema: CommandSchema = {
  action: 'send',
  description: 'Send an event to an element',
  category: 'event',
  primaryRole: 'event',
  // `send evt to #target` → args ['evt'], modifiers { to, detail }.
  // `detail` is a runtime contract key, not a preposition.
  ast: { args: ['event'], modifiers: { to: 'destination', detail: 'patient' } },
  roles: [
    {
      role: 'event',
      description: 'The event to send',
      required: true,
      expectedTypes: EVENT_NAME_TYPES, // identifiers tokenize as expression
      svoPosition: 1,
      sovPosition: 2,
      // bn marks the event sent accusatively, as trigger's: with the
      // profile-wide event marker, `#x তে স্ক্রোল তে পাঠান` read `scroll to #x`
      // and lost the send (was OPEN_ITEMS P50). The old marker still reads.
      markerOverride: { bn: 'কে' },
      markerLegacy: { bn: ['তে'] },
    },
    {
      role: 'destination',
      description: 'The target element (defaults to me)',
      required: false,
      // `expression` admits a bare-identifier target (`send "hello" to
      // ChatSocket`): the identifier tokenizes as expression, so
      // [selector, reference] rejected the marked `to ChatSocket` phrase and
      // the destination silently defaulted to `me` — in the en reference AND
      // most translations alike (socket-send ×23). Marker-guarded, same
      // precedent as add.destination (#571).
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 1,
      // send uses "to" not "on" for destination: send foo to #target
      markerOverride: {
        en: 'to',
        ja: 'に',
        ar: 'إلى',
        es: 'a',
        ko: '에게',
        zh: '到',
        // No leading hyphen: that is i18n's INTERNAL attachment convention
        // (`#count-ta` → `#countta`), not a surface form. The corpus the
        // transformer writes is spaced — tr `gönder #widget e`, qu
        // `#widget man ñitiy pi kachay` — so a hyphenated marker matched
        // nothing and rendered a stray `-e` / `-man` token.
        tr: 'e',
        pt: 'para',
        fr: 'à',
        de: 'an',
        id: 'ke',
        qu: 'man',
        sw: 'kwa',
      },
    },
  ],
};

// =============================================================================
// Batch 2 - Control Flow Commands
// =============================================================================

/**
 * If command: conditional execution.
 */
export const ifSchema: CommandSchema = {
  action: 'if',
  description: 'Conditional execution',
  category: 'control-flow',
  primaryRole: 'condition',
  // Bare-command form only — the real conditional path is
  // ASTBuilder.buildConditional, which never consults a command mapper.
  ast: { args: ['condition'] },
  hasBody: true,
  roles: [
    {
      role: 'condition',
      description: 'The condition to evaluate',
      required: true,
      expectedTypes: CONDITION_TYPES,
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Unless command: negated conditional execution.
 * Executes body when condition is false.
 */
export const unlessSchema: CommandSchema = {
  action: 'unless',
  description: 'Negated conditional execution (executes when condition is false)',
  category: 'control-flow',
  primaryRole: 'condition',
  // Bare-command form only; see the note on `if`.
  ast: { args: ['condition'] },
  hasBody: true,
  roles: [
    {
      role: 'condition',
      description: 'The condition to evaluate (body executes when false)',
      required: true,
      expectedTypes: CONDITION_TYPES,
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Else command: alternative branch.
 */
export const elseSchema: CommandSchema = {
  action: 'else',
  description: 'Else branch of conditional',
  category: 'control-flow',
  primaryRole: 'patient',
  hasBody: true,
  roles: [], // No roles - follows an if
};

/**
 * Repeat command: loop execution.
 */
export const repeatSchema: CommandSchema = {
  action: 'repeat',
  description: 'Repeat a block of commands',
  category: 'control-flow',
  primaryRole: 'loopType',
  // A flat loop head with no body; a loop with one builds via ASTBuilder.buildLoop.
  ast: { args: [['quantity', 'patient']] },
  hasBody: true,
  roles: [
    {
      role: 'loopType',
      description: 'Loop variant: forever, times, for, while, until, until-event',
      required: true,
      expectedTypes: ['literal'],
      svoPosition: 0,
      sovPosition: 0,
    },
    {
      role: 'quantity',
      description: 'Number of times to repeat',
      required: false,
      expectedTypes: ['literal', 'expression'],
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'event',
      description: 'Event to wait for (terminates loop)',
      required: false,
      expectedTypes: EVENT_NAME_TYPES,
      svoPosition: 2,
      sovPosition: 2,
    },
    {
      role: 'source',
      description: 'Element to listen for event on',
      required: false,
      expectedTypes: ['selector', 'reference'],
      svoPosition: 3,
      sovPosition: 3,
    },
  ],
  notes:
    'Can also use "repeat forever", "repeat until condition", or "repeat until event X from Y"',
};

/**
 * For command: iteration over collection.
 */
export const forSchema: CommandSchema = {
  action: 'for',
  description: 'Iterate over a collection',
  category: 'control-flow',
  primaryRole: 'patient',
  // `for item in items` → args [item], modifiers { in: items }.
  // The real loop path is ASTBuilder.buildLoop; this fires only for a
  // degenerate bare-command parse.
  ast: { args: ['patient'], modifiers: { in: 'source' } },
  hasBody: true,
  roles: [
    {
      role: 'patient',
      description: 'The iteration variable',
      required: true,
      // The transformer emits a bare identifier loop variable (`for item in …`),
      // which tokenizes as an expression — mirror the en handcrafted pattern.
      expectedTypes: FOR_VARIABLE_TYPES,
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'source',
      description: 'The collection to iterate over',
      required: true,
      expectedTypes: FOR_SOURCE_TYPES,
      svoPosition: 2,
      sovPosition: 1,
      // The i18n dicts translate the for-loop's `in` connective, NOT the
      // profile's source roleMarker — align each generated pattern to what the
      // transformer actually emits (`para item en $items`, `dla item w $items`).
      markerOverride: {
        en: 'in', // "for item in .items"
        es: 'en',
        fr: 'en',
        de: 'in',
        it: 'in',
        pt: 'dentro',
        id: 'dalam',
        ms: 'dalam',
        pl: 'w',
        ru: 'в',
        uk: 'у',
        sw: 'ndani',
        th: 'ใน',
        vi: 'trong',
        ar: 'في',
        tl: 'sa_loob',
        he: 'in', // the he transformer leaves the connective untranslated
        zh: '在',
      },
    },
  ],
};

/**
 * While command: conditional loop.
 */
export const whileSchema: CommandSchema = {
  action: 'while',
  description: 'Loop while condition is true',
  category: 'control-flow',
  primaryRole: 'condition',
  // A flat loop head with no body; a loop with one builds via ASTBuilder.buildLoop.
  ast: { args: ['condition'] },
  hasBody: true,
  roles: [
    {
      role: 'condition',
      description: 'The condition to check',
      required: true,
      expectedTypes: ['expression'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Continue command: skip to next iteration.
 */
export const continueSchema: CommandSchema = {
  action: 'continue',
  description: 'Continue to next loop iteration',
  category: 'control-flow',
  primaryRole: 'patient',
  // Roleless keyword: emits a bare command node.
  ast: {},
  roles: [], // No roles
  // Stands alone in a loop body (`repeat … if x continue end … end`). With no
  // pattern the body left it unread. Takes no arguments, like `exit`.
  bareKeyword: true,
};

// =============================================================================
// Batch 3 - DOM & Navigation Commands
// =============================================================================

/**
 * `url` is a globally-recognized tech acronym; we use it as the marker
 * keyword across all 24 supported languages to keep the parser surface
 * consistent with the core hyperscript grammar (`push url <X>`).
 * (Defined here, above its first schema reference — `goSchema` — to avoid TDZ.)
 */
const URL_MARKER_ALL_LANGS: Record<string, string> = {
  en: 'url',
  es: 'url',
  pt: 'url',
  fr: 'url',
  de: 'url',
  it: 'url',
  ja: 'url',
  ko: 'url',
  zh: 'url',
  ar: 'url',
  he: 'url',
  hi: 'url',
  bn: 'url',
  tr: 'url',
  ru: 'url',
  uk: 'url',
  pl: 'url',
  id: 'url',
  vi: 'url',
  th: 'url',
  ms: 'url',
  tl: 'url',
  sw: 'url',
  qu: 'url',
};

/**
 * `in new` is the marker half of `go … in new window`. Like `using view`, it
 * is a hyperscript phrase with no native translation, so all 24 languages use
 * the English form, and the trailing `window` is the role's captured value.
 *
 * (Defined here, above its first schema reference — `goSchema` — to avoid TDZ.)
 */
const IN_NEW_MARKER_ALL_LANGS: Record<string, string> = Object.fromEntries(
  Object.keys(URL_MARKER_ALL_LANGS).map(language => [language, 'in new'])
);

/**
 * Go command: navigates to a URL, or scrolls to an element.
 */
export const goSchema: CommandSchema = {
  action: 'go',
  description: 'Navigate to a URL, or scroll to an element',
  category: 'navigation',
  primaryRole: 'destination',
  roles: [
    {
      role: 'destination',
      description: 'The URL to navigate to, or the element to scroll to',
      required: true,
      // An element too: `go to #d1` scrolls #d1 into view on both engines.
      // Without `selector` the whole command was lost, in English and so in
      // every translation.
      expectedTypes: ['literal', ...ELEMENT_TARGET_TYPES],
      svoPosition: 1,
      sovPosition: 1,
      // "go to /page" (parsing). Directional, so the same locative-default
      // correction as `add`/`put`.
      //
      // Tier B (2.9): `go` is pure ALLATIVE — motion toward a target — so it
      // needs the directional in more languages than `add`/`put` do, including
      // ones where a container-locative was fine for those two.
      //   he — `אל` ("toward"), as `add`; `לך על url` read "go ON url".
      //   hi — `पर`: Hindi navigates to a page with `पर जाएं`; `में` is
      //        "go INTO", which is entering a place, not opening a URL.
      //   id — `ke`, as `add`.
      //   it — `a`: `andare a` for a specific target (`andare in` is for
      //        regions — `andare in Italia`).
      //   ru/uk — `на`: `перейти на сторінку` is the navigation idiom; `в`
      //        ("into") is right for `add`/`put` but not for opening a page.
      //   sw — `kwa`, as `add`.
      // th is NOT here — it renders bare, with zh and vi; see below.
      markerOverride: {
        en: 'to',
        es: 'a',
        ar: 'إلى',
        fr: 'à',
        de: 'zu',
        pt: 'para',
        he: 'אל',
        hi: 'पर',
        id: 'ke',
        it: 'a',
        ru: 'на',
        sw: 'kwa',
        uk: 'на',
      },
      // "go /page" (rendering — no preposition).
      //
      // zh, vi and th render BARE.
      //
      // zh and vi because their `go` keyword already encodes the direction, so
      // any destination marker is a second one: zh `前往` is "proceed-to"
      // (`前往 到 url` = "proceed-to to url") and vi `đi đến` is literally
      // "go to" (`đi đến vào url` = "go-to into url"). Both were corrected in
      // the i18n corpus in the same change (through fix-translations.sql,
      // since deleted: the semantic renderer writes every row now).
      //
      // th because Thai motion verbs take a BARE destination — `ไปบ้าน`
      // ("go home"), `ไปโรงเรียน` ("go school") — so `ไป url` is the idiomatic
      // form. The profile default rendered `ไป ใน url` ("go IN url"), which is
      // what needed fixing; the obvious replacement `ยัง` (giving the formal
      // `ไปยัง`) is rejected because `ยัง` is also the very common adverb
      // "still/yet", and the V4 vocab gate correctly refuses to classify it as
      // a particle — promoting it would mis-tokenize ordinary Thai.
      //
      // Parsing is unaffected for all three: none has a `markerOverride`, so
      // each stays on the profile-default branch and keeps accepting its old
      // markers (th `ใน` / `ไปยัง`) from the profile itself.
      renderOverride: { en: '', zh: '', vi: '', th: '' },
      // `go back` renders the destination bare in en (history nav has no `to`),
      // and he/zh render it with their PATIENT marker (לך את back / 前往 把 back)
      // while go-url keeps the destination marker (לך על url / 前往 到 url) —
      // the corpus is ground truth, so en's `to` is optional and he/zh accept
      // the patient particle as a destination-marker alternative, scoped to go.
      // The render side drops the preposition for these four, so the parse
      // side cannot require it: `go /page`, `前往 url`, `đi đến url`, `ไป url`
      // must parse alongside the marked forms the profile still accepts.
      markerOptional: { en: true, zh: true, vi: true, th: true },
      // zh renders `前往 把 back` with its PATIENT particle before go's
      // destination — a synonym here, not a distinct shape, so it is accepted as
      // a marker alternative scoped to go. he's `את` is the same thing and sits
      // in `markerLegacy` below: it moved there in #763 because the two fields
      // were then read by DIFFERENT branches, so leaving it here silently
      // stopped `לך את back` parsing the moment he gained a `markerOverride`.
      // Both fields now merge on both branches (`schemaMarkerAlternatives`), so
      // that trap is gone and the split is historical.
      markerVariants: { zh: ['把'] },
      markerLegacy: {
        es: ['en', 'sobre', 'hacia'],
        ar: ['على', 'في', 'ب'],
        fr: ['sur', 'dans'],
        de: ['auf', 'in'],
        pt: ['em', 'a'],
        // `את` is he's PATIENT particle, which the transformer renders before
        // go's destination in `go back` (`לך את back`) — a parse-only synonym
        // here, never rendered, which is exactly what markerLegacy is for.
        he: ['על', 'ב', 'ל', 'את'],
        hi: ['में'],
        id: ['pada', 'di'],
        it: ['in', 'su'],
        ru: ['в', 'к'],
        sw: ['kwenye'],
        uk: ['в', 'до'],
        // zh, vi and th are NOT listed: none has a markerOverride, so all three
        // stay on the profile-default branch and keep accepting their old
        // markers from the profile itself. Only their RENDERING changed.
        // Listing them here would be dead config — markerLegacy is read ONLY by
        // the override branch.
      },
    },
    {
      // `in new window`: a valueless flag, modeled as the view-transition tail
      // is (see VIEW_TRANSITION_MANNER_ROLE). Unmodeled, `in new` read as
      // `wait new` (the `in <duration>` form of wait), so `go to url /x in new
      // window` became a phantom wait and navigated the SAME window.
      role: 'manner',
      description: 'Open the URL in a new window (`in new window`)',
      required: false,
      // `window` is a reference in every language, and a literal or an
      // expression where a tokenizer reads it as a plain word.
      expectedTypes: ['reference', 'literal', 'expression'],
      valueShape: 'keyword',
      svoPosition: 2,
      sovPosition: 2,
      markerOverride: IN_NEW_MARKER_ALL_LANGS,
    },
  ],
  // `go to url "/page"` — without this variant the destination captures the
  // bare word `url` and the actual URL is dropped as tolerated-trailing text,
  // in en and therefore in every render (the go-url corpus row). The required
  // `url` literal keeps the variant inert for `go back` / scroll forms.
  rolePrefixLiteralVariants: [
    {
      role: 'destination',
      literal: URL_MARKER_ALL_LANGS,
      idSuffix: 'url',
      priorityDelta: 5,
      methodCarrier: 'method',
    },
  ],
};

/**
 * Transition command: animates element changes.
 */
export const transitionSchema: CommandSchema = {
  action: 'transition',
  description: 'Transition an element with animation',
  category: 'dom-visibility',
  primaryRole: 'patient',
  // `transition *color from blue to red over 500ms` → args ['*color'],
  // modifiers { from: source, to: goal, over: duration }. No destination: upstream
  // has no `transition … on <element>`; the element is the property's owner
  // (`#foo's *width`, `my opacity`), and `me` when it has none. The role this
  // schema used to carry only ever held that implicit `me`, and in SOV languages
  // whose destination markers include the genitive (ja `の`, ko `의`, tr `nin`,
  // qu `pa`) it read the owner of an of-possessive (`*width nin #foo`) as an
  // element, silently.
  ast: {
    args: ['patient'],
    modifiers: { from: 'source', to: 'goal', over: 'duration' },
  },
  // Goal-less `transition {patient} over {duration}` (slide-toggle) parses via
  // a separate generated variant — see the goal role's required NOTE.
  omitRoleVariants: ['goal'],
  roles: [
    {
      role: 'patient',
      description: 'The property to transition (opacity, *background-color, etc.)',
      required: true,
      // A bare CSS property name (`transition opacity to 0`) tokenizes as an
      // identifier → expression, so a literal-only patient rejected the idiomatic
      // unquoted form in EVERY language: en (and 8 others) dropped the whole
      // command, while the 14 languages whose body walkers recover it via
      // verb-anchoring parsed it — and were then precision-penalized as
      // "spurious transition" (×66, the largest R0-precision family) against the
      // en reference that had silently dropped it. Admitting `expression` (the
      // add/send.destination precedent, #571/#573) lets matchBest capture the
      // property in all languages. `selector` is admitted for the style-property
      // syntax ONLY: `*background-color` / `*max-height` tokenize as selector
      // (transition-color, slide-toggle — en dropped both), and translations
      // already capture patient:selector for them via the lax body walkers.
      // 'property-path' opts it into the "of"-possessive matcher: every language
      // but English writes `#foo's *width` as `*width de #foo` (tr `*width nin
      // #foo`), which no other reader takes — es/de/ar/fr refused it, and tr/qu
      // read `*width` as the destination and `#foo` as the patient, silently.
      expectedTypes: ['literal', 'expression', 'selector', 'property-path'],
      svoPosition: 1,
      sovPosition: 2,
      // No marker before the CSS property name (SVO/VSO languages)
      // SOV languages (bn, qu) use their default patient marker from profile
      markerOverride: { en: '', ar: '', tl: '', sw: '' },
    },
    {
      role: 'source',
      description: 'The value to transition from (`from 0px to 100px`)',
      required: false,
      expectedTypes: ['literal', 'expression'],
      svoPosition: 1.5,
      sovPosition: 2.5,
    },
    {
      role: 'goal',
      description: 'The target value to transition to',
      // Required, deliberately: goal-less transition (`transition *max-height
      // over 300ms`) is NOT valid in either engine (both require `to <value>`;
      // measured 2026-09-25, when the slide-toggle corpus row that used it was
      // corrected), but it is accepted leniently here, and making goal
      // optional wraps its marker group as skippable and the bare value then
      // re-binds by particle metadata (`a 0` → destination:literal=0),
      // clobbering goal+duration capture in every marker language — verified
      // regression, do not repeat. The goal-less form is covered by the
      // SEPARATE `omitRoleVariants: ['goal']` pattern instead (no skippable
      // group to re-bind).
      required: true,
      expectedTypes: ['literal', 'expression'],
      svoPosition: 2,
      sovPosition: 3,
      // "to" preposition before goal value. These MUST match what the i18n
      // transformer actually renders (the corpus ground truth) — de 'auf' and
      // sw 'kwenye' were aspirational, never matched a rendered text, so the
      // generated pattern could not fire and the whole command was dropped
      // (the NO-TRANSITION half of the spurious-transition ×66 precision
      // family). SOV languages (ja/ko/tr/bn/qu) reach transition via the fused
      // grammar-transformed paths (this override is position-BEFORE, so it
      // cannot capture their postposed `0 に` forms anyway); their entries are
      // kept aligned to the rendered particles for the pattern renderer.
      markerOverride: {
        en: 'to',
        ar: 'إلى',
        tl: 'sa',
        sw: 'kwa',
        bn: 'তে',
        qu: 'man',
        es: 'a',
        pt: 'para',
        fr: 'à',
        de: 'zu',
        he: 'על',
        it: 'in',
        ja: 'に',
        ko: '에',
        ms: 'ke',
        pl: 'do',
        ru: 'в',
        th: 'ใน',
        tr: 'e',
        uk: 'в',
        vi: 'vào',
        zh: '到',
        id: 'ke',
      },
    },
    {
      role: 'duration',
      description: 'Transition duration (over 500ms, for 2 seconds)',
      required: false,
      expectedTypes: ['literal'],
      svoPosition: 4,
      sovPosition: 4,
      markerOverride: { en: 'over' }, // "transition opacity to 1 over 500ms"
    },
    {
      role: 'style',
      description: 'Easing function (ease-in, linear, etc.)',
      required: false,
      expectedTypes: ['literal'],
      svoPosition: 5,
      sovPosition: 5,
      // Upstream spells it `using "<css transition>"`; `with` is not transition
      // grammar in either engine.
      markerOverride: { en: 'using' },
    },
  ],
};

/**
 * `start [a] view transition [using "<type>"] <commands> end`: upstream's block,
 * whose body runs as the update of `document.startViewTransition` (the engine's
 * `commands/animation.ts`). The opener is written in English in all 24
 * languages, as the `using view transition` tail already is
 * (USING_VIEW_MARKER_ALL_LANGS: a hyperscript-specific phrase with no native
 * translation), so no profile carries a keyword and nothing is generated:
 * `patterns/view-transition.ts` is every language's pattern (`structuralOnly`).
 * The clause walker nests the body under the head as it nests a loop's
 * (ViewTransitionSemanticNode).
 */
export const viewTransitionSchema: CommandSchema = {
  action: 'viewTransition',
  description: 'Run the body inside a view transition',
  category: 'dom-visibility',
  primaryRole: 'style',
  hasBody: true,
  structuralOnly: true,
  roles: [
    {
      role: 'style',
      description: 'The transition type (`using "slide"`), passed as `types: [type]`',
      required: false,
      // The engine reads a STRING only (`p.matchType('STRING')`).
      expectedTypes: VIEW_TRANSITION_STYLE_TYPES,
      svoPosition: 1,
      sovPosition: 1,
      markerOverride: { en: 'using' },
    },
  ],
};

/**
 * Clone command: clones an element.
 */
export const cloneSchema: CommandSchema = {
  action: 'clone',
  description: 'Clone an element',
  category: 'dom-content',
  primaryRole: 'patient',
  // `clone #tpl into #container` → args ['#tpl'], modifiers { into: '#container' }.
  ast: { args: [['source', 'patient']], modifiers: { into: 'destination' } },
  roles: [
    {
      role: 'patient',
      description: 'The element to clone',
      required: true,
      expectedTypes: ['selector', 'reference'],
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'destination',
      description: 'Where to put the clone',
      required: false,
      expectedTypes: ['selector', 'reference'],
      svoPosition: 2,
      sovPosition: 1,
      markerOverride: { en: 'into' }, // "clone #element into #container"
    },
  ],
};

/**
 * Focus command: focuses an element.
 */
export const focusSchema: CommandSchema = {
  action: 'focus',
  description: 'Focus an element',
  category: 'dom-content',
  primaryRole: 'patient',
  // `focus #input` → args ['#input']. The original mapper built an always-empty
  // modifiers object, so no modifier key is emitted.
  ast: { args: [['destination', 'patient']] },
  roles: [
    {
      role: 'patient',
      description: 'The element to focus (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Blur command: removes focus from an element.
 */
export const blurSchema: CommandSchema = {
  action: 'blur',
  description: 'Remove focus from an element',
  category: 'dom-content',
  primaryRole: 'patient',
  // `blur #input` → args ['#input'].
  ast: { args: [['destination', 'patient']] },
  roles: [
    {
      role: 'patient',
      description: 'The element to blur (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Empty command: clears innerHTML of an element (upstream _hyperscript 0.9.90).
 *
 * Patterns:
 * - EN: empty #container
 * - ES: vaciar #container
 */
export const emptySchema: CommandSchema = {
  action: 'empty',
  description: 'Remove all children from an element (sets innerHTML to empty)',
  category: 'dom-content',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The element to empty (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Open command: opens a <dialog>, <details> element, or popover (upstream 0.9.90).
 * Optional `as modal` / `as non-modal` variant uses the `style` role.
 *
 * Patterns:
 * - EN: open #confirm-dialog
 * - EN: open #confirm-dialog as modal
 */
export const openSchema: CommandSchema = {
  action: 'open',
  description: 'Open a dialog, details element, or popover',
  category: 'dom-content',
  primaryRole: 'patient',
  // `open #dlg as non-modal` → args ['#dlg'], modifiers { as: 'non-modal' }.
  // `OpenCommand.parseInput` reads the element from `args[0]` and the variant
  // from the `as` modifier, and `as` is also style's English marker, so the key
  // needs no consistency-gate exemption.
  ast: { args: ['patient'], modifiers: { as: 'style' } },
  roles: [
    {
      role: 'patient',
      description: 'The element to open (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'style',
      description: 'Open-mode variant (modal / non-modal) for <dialog>',
      required: false,
      expectedTypes: ['literal', 'expression'],
      // The marker FOLLOWS the target: `open #dlg as non-modal`, which is
      // OpenCommand's own documented example. Style used to sit at svoPosition
      // 1, generating `open [as {style}] [{patient}]` — so only the un-English
      // `open as modal #dlg` bound the role, and the documented surface parsed
      // to `patient` alone with the variant silently dropped.
      svoPosition: 2,
      sovPosition: 1,
      // `as` in en; `として` ("as/qua") in ja. ja's profile style marker is the
      // instrumental particle `で`, which is ALSO its event marker, so the
      // rendered `modal で #dlg を 開く` reads as an event-handler head and the
      // whole line parsed as `on modal` rather than `open` — a fresh break the
      // 23-language round-trip probe caught, since making the role bind at all
      // is what first put a style phrase in front of a ja verb.
      //
      // ja ONLY: the probe (`translate(en→L)` → `parse(L)`, both variants, all
      // 23 languages) shows every other language already round-trips both
      // `modal` and `non-modal` on its existing marker. ko `로서` and tr
      // `olarak` were tried and reverted — they changed the rendered surface
      // without fixing anything.
      markerOverride: { en: 'as', ja: 'として' },
    },
  ],
};

/**
 * Close command: closes a <dialog>, <details> element, or popover (upstream 0.9.90).
 */
export const closeSchema: CommandSchema = {
  action: 'close',
  description: 'Close a dialog, details element, or popover',
  category: 'dom-content',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The element to close (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Select command: selects text contents of an <input>/<textarea>, or DOM element content (upstream 0.9.90).
 */
export const selectSchema: CommandSchema = {
  action: 'select',
  description: 'Select the contents of a text field or DOM element',
  category: 'dom-content',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The element whose contents to select (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Clear command: clears a variable or an input-element's value (upstream 0.9.90).
 */
export const clearSchema: CommandSchema = {
  action: 'clear',
  description: 'Clear a variable, local, or input element',
  category: 'variable',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The variable or element to clear (defaults to me)',
      required: false,
      expectedTypes: ['selector', 'reference', 'expression'],
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Reset command: resets a <form> element to its default values (upstream 0.9.90).
 */
export const resetSchema: CommandSchema = {
  action: 'reset',
  description: 'Reset a <form> element to its default values',
  category: 'dom-content',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The form element to reset (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Breakpoint command: drops into the debugger (emits a `debugger;` statement).
 * Zero-arg. Upstream 0.9.90.
 */
export const breakpointSchema: CommandSchema = {
  action: 'breakpoint',
  description: 'Drop into the debugger',
  category: 'control-flow',
  primaryRole: 'patient',
  roles: [],
  // Roleless: without a bare-keyword pattern NO pattern is generated at all
  // (getDefinedSchemas filters zero-role schemas), so `on click breakpoint
  // then …` silently drops the breakpoint in every generated-path language —
  // while lax event-fused paths (bn/hi/ja/ko/qu/th) keep it, reading as
  // spurious vs the en reference. Same standalone-safety argument as `exit`:
  // zero-arg, the single-keyword match can't swallow anything.
  bareKeyword: true,
};

// =============================================================================
// Batch 4 - Advanced Commands
// =============================================================================

/**
 * Call command: calls a function.
 */
export const callSchema: CommandSchema = {
  action: 'call',
  description: 'Call a function',
  category: 'control-flow',
  primaryRole: 'patient',
  // `call fn()` → args [fn()].
  ast: { args: ['patient'] },
  roles: [
    {
      role: 'patient',
      description: 'The function to call',
      required: true,
      expectedTypes: ['expression', 'reference'],
      // A function-call patient (`validateForm()`) is an expression, not a definite
      // DOM object, so the transformer emits it UNMARKED in a multi-command body
      // (`קרא validateForm()` / `调用 validateForm()`) — but a marked patient is
      // still emitted elsewhere. The he/zh patient marker (את / 把) is made OPTIONAL
      // so both forms parse; without it `call` dropped (form-submit-prevent lossy).
      // Scoped to he/zh — SOV call (ja を / ko 을) already parses, so leaving their
      // marker required avoids relaxing their role typing.
      markerOptional: { he: true, zh: true },
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Return command: returns a value.
 */
export const returnSchema: CommandSchema = {
  action: 'return',
  description: 'Return a value from a function',
  category: 'control-flow',
  primaryRole: 'patient',
  // `return value` → args [value].
  ast: { args: ['patient'] },
  roles: [
    {
      role: 'patient',
      description: 'The value to return',
      required: false,
      expectedTypes: ['literal', 'expression', 'reference', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * JS command: executes raw JavaScript.
 */
export const jsSchema: CommandSchema = {
  action: 'js',
  description: 'Execute raw JavaScript code',
  category: 'control-flow',
  primaryRole: 'patient',
  // `js ... end` → args [code]. Note core's parser skips semantic parsing
  // for js entirely; this shape only fires on a direct buildAST call.
  ast: { args: ['patient'] },
  hasBody: true,
  roles: [
    {
      role: 'patient',
      description: 'The JavaScript code to execute',
      required: false,
      expectedTypes: ['expression'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Tell command: sends commands to another element.
 */
export const tellSchema: CommandSchema = {
  action: 'tell',
  description: 'Execute commands in context of another element',
  category: 'control-flow',
  primaryRole: 'destination',
  // `tell #el` → args ['#el']. The body is attached by ASTBuilder, not here.
  ast: { args: [['destination', 'patient']] },
  hasBody: true,
  roles: [
    {
      role: 'destination',
      description: 'The element to tell',
      required: true,
      expectedTypes: ELEMENT_TARGET_TYPES,
      svoPosition: 1,
      sovPosition: 1,
      // "tell #element ..." (no preposition in en). Object-marking targets mark
      // tell's object with their accusative/BA particle — Hebrew את (`אמור את
      // #element`), Chinese 把 (`告诉 把 #modal`) — so the generated pattern must
      // expect it; without it the marker token breaks the match and `tell` is
      // dropped (tell-command / tell-other-element lossy in he and zh).
      markerOverride: { en: '', he: 'את', zh: '把' },
    },
  ],
};

/**
 * Default command: sets default values.
 */
export const defaultSchema: CommandSchema = {
  action: 'default',
  description: 'Set a default value for a variable',
  category: 'variable',
  primaryRole: 'destination',
  // `default :x to 0` → args [:x], modifiers { to: 0 }. Same arg/modifier
  // inversion as `set`: DefaultCommand's parseInput reads the target from
  // `args[0]` and the value from `modifiers.to`, while the parser binds the
  // variable to `destination` and the value to `patient`.
  ast: { args: ['destination'], modifiers: { to: 'patient' } },
  roles: [
    {
      role: 'destination',
      description: 'The variable to set default for',
      required: true,
      // Full default-value drill (session 12): property-path opts this role
      // into the possessive matchers (`default my @data-count to "0"`),
      // exactly parallel to set's destination. Enriched together with the
      // per-language markers below so all 24 languages align in one step
      // (an en-only enrichment would have minted honest-dip A/B entries in
      // every still-dropping language).
      expectedTypes: ['selector', 'reference', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
      // Same overrides as SET: no marker before variable in VSO/SVO;
      // SOV languages mark the variable with their patient/accusative marker.
      markerOverride: {
        en: '',
        ar: '',
        tl: '',
        sw: '',
        bn: 'কে',
        qu: 'ta',
        ja: 'を',
        ko: '를',
        tr: 'i',
        hi: 'को',
        de: '',
        es: '',
        fr: '',
        it: '',
        pt: '',
        pl: '',
        ru: '',
        uk: '',
        vi: '',
        id: '',
        ms: '',
        th: '',
        zh: '把',
        he: 'את',
      },
    },
    {
      role: 'patient',
      description: 'The default value',
      required: true,
      expectedTypes: ['literal', 'expression', 'property-path'],
      svoPosition: 2,
      sovPosition: 2,
      // Same shape as SET: the value carries the language's "to" marker.
      // Markers mirror what the transformer actually renders for default
      // (dict-driven), which differs from set in places (de zu vs auf,
      // sw kwa vs kwenye).
      markerOverride: {
        en: 'to',
        ar: 'إلى',
        tl: 'sa',
        sw: 'kwa',
        bn: 'তে',
        qu: 'man',
        ja: 'に',
        ko: '에',
        tr: 'e',
        hi: 'में',
        de: 'zu',
        es: 'a',
        fr: 'à',
        it: 'in',
        pt: 'para',
        pl: 'do',
        ru: 'в',
        uk: 'в',
        vi: 'vào',
        id: 'ke',
        ms: 'ke',
        th: 'ใน',
        zh: '到',
        he: 'על',
      },
    },
  ],
};

/**
 * Init command: initialization block.
 */
export const initSchema: CommandSchema = {
  action: 'init',
  description: 'Initialization block that runs once',
  category: 'control-flow',
  primaryRole: 'patient',
  // Roleless keyword: emits a bare command node.
  ast: {},
  hasBody: true,
  roles: [],
};

/**
 * Behavior command: defines reusable behavior.
 * Patterns:
 * - EN: behavior Draggable
 * - EN: behavior Draggable(dragHandle)
 */
export const behaviorSchema: CommandSchema = {
  action: 'behavior',
  description: 'Define a reusable behavior',
  category: 'control-flow',
  primaryRole: 'patient',
  // `behavior Name` → args [Name].
  ast: { args: ['patient'] },
  hasBody: true,
  roles: [
    {
      role: 'patient',
      description: 'The behavior name (PascalCase identifier)',
      required: true,
      expectedTypes: ['literal', 'reference', 'expression'], // expression for PascalCase identifiers
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Install command: installs a behavior on an element.
 * Patterns:
 * - EN: install Draggable
 * - EN: install Draggable(dragHandle: .titlebar)
 * - EN: install Draggable on #element
 */
export const installSchema: CommandSchema = {
  action: 'install',
  description: 'Install a behavior on an element',
  category: 'control-flow',
  primaryRole: 'patient',
  // `install Draggable on #el` → args [Draggable], modifiers { on: '#el' }.
  ast: { args: ['patient'], modifiers: { on: 'destination' } },
  roles: [
    {
      role: 'patient',
      description: 'The behavior name to install',
      required: true,
      expectedTypes: ['literal', 'reference', 'expression'],
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'destination',
      description: 'Element to install on (defaults to me)',
      required: false,
      expectedTypes: ['selector', 'reference'],
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 1,
    },
  ],
};

/**
 * Measure command: measures element dimensions or position.
 * Patterns:
 * - EN: measure x
 * - EN: measure width
 * - EN: measure width of #element
 */
export const measureSchema: CommandSchema = {
  action: 'measure',
  description: 'Measure element dimensions (x, y, width, height, etc.)',
  category: 'dom-content',
  primaryRole: 'patient',
  // `measure width of #el` → args ['width'], modifiers { of: '#el' }.
  // The element arrives as destination or source depending on the pattern.
  ast: { args: ['patient'], modifiers: { of: ['destination', 'source'] } },
  roles: [
    {
      role: 'patient',
      // The engine reads one expression after `measure`: a measurement name
      // (`measure width`), a property of an element (`measure #el's top`), or
      // the element itself (`measure #el`, `measure me`). Typed for names only,
      // the element was left unread: `measure #other` became `measure`.
      description: 'Property to measure (x, y, width, …), or the element itself',
      required: false, // Plain "measure" is valid, defaults to bounds
      expectedTypes: ['literal', 'expression', 'selector', 'reference'],
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'source',
      description: 'Element to measure (defaults to me)',
      required: false,
      expectedTypes: ELEMENT_TARGET_TYPES,
      default: { type: 'reference', value: 'me' },
      svoPosition: 2,
      sovPosition: 2,
      markerOverride: { en: 'of' }, // "measure width of #element"
    },
  ],
};

// =============================================================================
// Batch 5 - DOM Content Manipulation
// =============================================================================

/**
 * `using view` is the marker half of hyperscript's `using view transition`
 * tail (swap/process). Like `partials in` and `url`, it is a
 * hyperscript-specific phrase with no native translation, so all 24 languages
 * use the English form. The multi-word marker is split into separate literal
 * tokens by the pattern generator; the trailing `transition` is the role's
 * captured value.
 *
 * (Defined here, above its first schema reference — `swapSchema` — to avoid TDZ.)
 */
const USING_VIEW_MARKER_ALL_LANGS: Record<string, string> = {
  en: 'using view',
  es: 'using view',
  pt: 'using view',
  fr: 'using view',
  de: 'using view',
  it: 'using view',
  ja: 'using view',
  ko: 'using view',
  zh: 'using view',
  ar: 'using view',
  he: 'using view',
  hi: 'using view',
  bn: 'using view',
  tr: 'using view',
  ru: 'using view',
  uk: 'using view',
  pl: 'using view',
  id: 'using view',
  vi: 'using view',
  th: 'using view',
  ms: 'using view',
  tl: 'using view',
  sw: 'using view',
  qu: 'using view',
};

/**
 * The shared `manner` role behind `using view transition` (swap, process).
 *
 * The surface is a valueless FLAG in hyperfixi's tail form — the runtime tests
 * only for presence (`raw.modifiers?.viewTransition !== undefined`), never the
 * value. But a role with no value slot is not expressible: `buildRoleToken`
 * always emits a value token after a marker. So the marker takes the first two
 * words and the third, `transition`, is captured as the role's literal value.
 *
 * `valueShape: 'keyword'` is what makes the optional trailing slot free: without
 * it an uncaptured slot weighs into `scoreRoleCoverage`'s denominator and drops
 * plain `process partials in it` / `swap #a with #b` below the 0.7 adoption
 * threshold — the toggle-es regression class (see {@link RoleSpec.valueShape}).
 *
 * NO default: the runtime treats presence as the request, so a default would
 * turn every swap and every process into a view transition.
 */
const VIEW_TRANSITION_MANNER_ROLE: Omit<RoleSpec, 'svoPosition' | 'sovPosition'> = {
  role: 'manner',
  description: 'Run the swap inside a `document.startViewTransition()` (`using view transition`)',
  required: false,
  // Both types, because the SAME word tokenizes differently per language and
  // the difference is an accident of vocabulary: `transition` is a command
  // keyword in en/fr (it is also their verb) and a bare identifier everywhere
  // else, which types it `literal` in the first group and `expression` in the
  // second. Measured: with `['literal']` alone only en and fr captured the
  // tail. Widening is safe precisely because the slot is marker-guarded —
  // nothing reaches it until `using view` has matched.
  expectedTypes: ['literal', 'expression'],
  valueShape: 'keyword',
  markerOverride: USING_VIEW_MARKER_ALL_LANGS,
};

/**
 * Swap command: swaps DOM content using various strategies.
 *
 * Patterns:
 * - EN: swap innerHTML of #target
 * - EN: swap delete #target
 * - EN: swap beforebegin #target with <html>
 */
export const swapSchema: CommandSchema = {
  action: 'swap',
  description: 'Swap DOM content using various strategies (innerHTML, outerHTML, delete, etc.)',
  category: 'dom-content',
  primaryRole: 'destination',
  // SwapCommand's contract is keyword-positional ARGS, with no modifier read at
  // all (`commands/dom/swap.ts` parseInput takes `const args = raw.args` and
  // never touches `raw.modifiers`). It scans the arg list for `with`/`of`/
  // `delete`/`using`/`into`/`over` and otherwise falls back to
  // target = args[len-2], content = args[len-1], strategy = args[0] when that
  // names one. So the three roles this schema binds have to arrive positionally
  // in method → destination → patient order.
  //
  // The previous descriptor read `source`/`style` — roles no swap pattern binds
  // — and routed `destination` into `modifiers.on`, so every modifier it emitted
  // was dead end to end: `swap #a with #b` reached the runtime as a one-arg node
  // and `swap delete #t` as a zero-arg one, and both threw before doing any
  // work. Preserved verbatim by the Arc F migration (a faithful migration must
  // not change output) and pinned as two `drift` exemptions; this is the
  // behavior fix those entries were waiting for.
  //
  // `manner` is the exception to "keyword-positional args": it rides as
  // `modifiers.viewTransition`, which SwapCommand.parseInput reads for presence
  // alongside its existing flat-args `using`/`view`/`transition` scan.
  ast: { args: ['method', 'destination', 'patient'], modifiers: { viewTransition: 'manner' } },
  roles: [
    {
      role: 'method',
      description: 'The swap strategy (innerHTML, outerHTML, beforebegin, afterend, delete)',
      required: false,
      expectedTypes: ['literal'],
      svoPosition: 1,
      sovPosition: 3,
      markerOverride: { en: '' }, // "swap innerHTML ..." (no preposition)
    },
    {
      role: 'destination',
      description: 'The element to swap content in/for',
      required: true,
      // What upstream swaps: an element, a variable, or a property of one
      // (`swap #a's textContent with my textContent`).
      expectedTypes: [...ELEMENT_TARGET_TYPES, 'property-path'],
      svoPosition: 2,
      sovPosition: 1,
      // The i18n transformer emits the element-swap shape `swap X with Y` with
      // an UNMARKED destination right after the verb (`intercambiar #a con #b`),
      // not the profile's destination preposition (`intercambiar en #a`), so the
      // generated patterns never matched any transformer output. SVO languages
      // override to the emission shape: bare destination (he marks it with the
      // direct-object particle את, zh with 把).
      markerOverride: {
        en: 'of', // "swap innerHTML of #target"
        es: '',
        fr: '',
        pt: '',
        it: '',
        de: '',
        pl: '',
        ru: '',
        uk: '',
        id: '',
        ms: '',
        sw: '',
        th: '',
        vi: '',
        he: 'את', // "החלף את #a עם #b" — direct-object particle
        zh: '把', // "交换 把 #a 用 #b" — BA object marker
        // tl was the one language with a patient with-word (`nang`, below) and
        // no destination entry, so it fell to its profile destination marker and
        // rendered `palitan_pwesto sa #a nang #b`. `sa` there is read as the
        // role marker it is, both selectors bind to the wrong slots, and the
        // parse came back `swap with destination` — both roles gone
        // (tl swap-content). The i18n row is unmarked, like every other SVO/VSO
        // language here.
        tl: '',
      },
    },
    {
      role: 'patient',
      description: 'The content to swap in (optional for delete)',
      required: false,
      // The other side of `swap X with Y`, which upstream reads as the same kind
      // of target: `me` and a `:var` are references, `my textContent` a property
      // path. Without them, every language but en/ja/ko dropped the operand
      // (`intercambiar #target con yo` read back as `swap #target`).
      expectedTypes: ['literal', 'expression', 'selector', 'reference', 'property-path'],
      svoPosition: 3,
      sovPosition: 2,
      // Patient takes each language's with-word (matching the i18n dicts'
      // `with` emission), so `swap {destination} with {patient}` parses.
      markerOverride: {
        en: 'with', // "swap innerHTML of #target with <html>"
        es: 'con',
        fr: 'avec',
        pt: 'com',
        it: 'con',
        de: 'mit',
        pl: 'z',
        ru: 'с',
        uk: 'з',
        id: 'dengan',
        ms: 'dengan',
        sw: 'na',
        th: 'ด้วย',
        vi: 'với',
        he: 'עם',
        zh: '用',
        // SOV/postpositional with-words. These follow the patient (`#b से`,
        // `#b দিয়ে`), matching the i18n `with` emission. Without them the SOV
        // patient-first swap pattern's trailing group (which binds the second
        // element to `destination`) had only the locative dest-marker (hi में,
        // bn তে) as its alternatives, so `#b <with-word>` never bound and #b
        // dropped — hi/bn/tr/qu rendered the invalid `swap with #a`. ja/ko
        // escaped only because their dest-marker alternatives already carry the
        // instrumental (で / 로). See generateSOVPatientFirstEventHandlerPattern.
        hi: 'से',
        bn: 'দিয়ে',
        tr: 'ile',
        qu: 'wan',
        // VSO with-words. The corpus puts the with-element AFTER the event
        // (`استبدل #a عند نقر بـ#b`, `palitan_pwesto #a kapag click nang #b`);
        // the vso-verb-first generator's swap-gated trailing group binds it to
        // `destination` via these words. ar's `بـ` is the bi-proclitic + tatweel
        // exactly as the ArabicProcliticExtractor emits it (glued to a selector
        // sigil). See generateVSOVerbFirstEventHandlerPattern.
        ar: 'بـ',
        tl: 'nang',
      },
    },
    {
      ...VIEW_TRANSITION_MANNER_ROLE,
      svoPosition: 4,
      sovPosition: 4,
    },
  ],
};

/**
 * Morph command: morphs one element into another using DOM diffing.
 *
 * Patterns:
 * - EN: morph #target to <html>
 * - EN: morph #list to it
 *
 * Role layout matches the i18n transformer's marking (the element being
 * morphed takes the object/patient marker — ja を, ko 를, tr i — and the
 * content target takes the destination marker — ja に, ko 에, tr e), so the
 * generated patterns and the SOV verb-anchoring captures agree. `reference`
 * on destination admits the then-chained `morph #list to it` corpus shape.
 */
export const morphSchema: CommandSchema = {
  action: 'morph',
  description: 'Morph an element into another using DOM diffing',
  category: 'dom-content',
  primaryRole: 'patient',
  // `morph #list to it` → args [content], modifiers { on: element }.
  // Content is source or destination; the element being morphed is the patient.
  //
  // `manner` rides as `modifiers.viewTransition`, same as swap/process —
  // MorphCommand.parseInput reads it for presence alongside its existing
  // flat-args `using`/`view`/`transition` scan (the traditional-parser path,
  // which owns English because morph sits on SKIP_SEMANTIC_COMMANDS).
  ast: {
    args: [['source', 'destination']],
    modifiers: { on: 'patient', viewTransition: 'manner' },
  },
  roles: [
    {
      role: 'patient',
      description: 'The element to morph',
      required: true,
      expectedTypes: ELEMENT_TARGET_TYPES,
      svoPosition: 1,
      sovPosition: 1,
      markerOverride: { en: '' }, // "morph #target ..." (no preposition)
    },
    {
      role: 'destination',
      description: 'The target content/element to morph into',
      required: true,
      expectedTypes: ['literal', 'expression', 'selector', 'reference'],
      svoPosition: 2,
      sovPosition: 2,
      markerOverride: { en: 'to' }, // "morph #target to it"
    },
    {
      // `morph #target to it using view transition` — the same tail as swap and
      // process, closed here for the same reason (#870's third-schema filing):
      // without the role the semantic parser matched morph at confidence 1.0
      // and stranded the tail, so every semantic-only consumer (multilingual
      // bundles, the bridge, `translate()`) silently dropped the animation
      // request in all 24 languages. Morph has NO hand-written patterns in any
      // language, so this schema role is the whole fix on the pattern side.
      ...VIEW_TRANSITION_MANNER_ROLE,
      svoPosition: 3,
      sovPosition: 3,
    },
  ],
};

// =============================================================================
// Batch 6 - Missing Commands (beep, break, copy, exit, pick, render)
// =============================================================================

/**
 * Beep command: debug output with type information.
 * Syntax: beep! / beep! <expression>
 */
export const beepSchema: CommandSchema = {
  action: 'beep',
  description: 'Debug output for expressions with type information',
  category: 'variable',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The expression(s) to debug',
      required: false,
      expectedTypes: ['literal', 'selector', 'reference', 'expression'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * `ask <message>`: a prompt; the reply is `it` (`ask "Name?" then put it into
 * #out`). Upstream's command (`askAnswer` in the engine's commands/misc.ts).
 */
export const askSchema: CommandSchema = {
  action: 'ask',
  description: 'Prompt the user for a value (the reply is `it`)',
  category: 'variable',
  primaryRole: 'patient',
  // `ask "Name?"` → args ['Name?'].
  ast: { args: ['patient'] },
  roles: [
    {
      role: 'patient',
      description: 'The question',
      required: true,
      expectedTypes: ['literal', 'reference', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * `answer <message>` alerts; `answer <message> with <a> or <b>` confirms, and
 * `it` is the chosen value. The choices are one `style` role, whose marker is
 * `with` in every profile (es `con`, ja `で`, ko `로`): an expression the value
 * pass writes with the language's `or`.
 */
export const answerSchema: CommandSchema = {
  action: 'answer',
  description: 'Show a message, or ask a yes/no question with two choices',
  category: 'variable',
  primaryRole: 'patient',
  // `answer "Save?" with "Yes" or "No"` → args ['Save?'], modifiers { with }.
  ast: { args: ['patient'], modifiers: { with: 'style' } },
  roles: [
    {
      role: 'patient',
      description: 'The message',
      required: true,
      expectedTypes: ['literal', 'reference', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'style',
      description: 'The two choices (`with "Yes" or "No"`)',
      required: false,
      expectedTypes: ['expression', 'literal'],
      svoPosition: 2,
      sovPosition: 2,
    },
  ],
};

/**
 * Break command: exits from the current loop.
 * Syntax: break
 */
export const breakSchema: CommandSchema = {
  action: 'break',
  description: 'Exit from the current loop',
  category: 'control-flow',
  primaryRole: 'patient',
  roles: [],
  // Stands alone in a loop body, as `continue` does.
  bareKeyword: true,
};

/**
 * Copy command: copies text or element content to clipboard.
 * Syntax: copy <source>
 */
export const copySchema: CommandSchema = {
  action: 'copy',
  description: 'Copy text or element content to the clipboard',
  category: 'dom-content',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The text or element to copy',
      required: true,
      expectedTypes: ['literal', 'selector', 'reference', 'expression', 'property-path'],
      svoPosition: 1,
      sovPosition: 1,
    },
  ],
};

/**
 * Exit command: exits early from an event handler.
 * Syntax: exit
 */
export const exitSchema: CommandSchema = {
  action: 'exit',
  description: 'Exit from the current event handler',
  category: 'control-flow',
  primaryRole: 'patient',
  roles: [],
  // A guard-clause `exit` stands alone inside a conditional branch (`if item
  // is null exit end`, behavior-sortable). Without a bare-keyword pattern the
  // branch parses to NOTHING, the conditional fold rejects (empty then-branch),
  // and the flat `if` head truncates the condition — whose leftover predicate
  // word then re-anchors as a phantom command in languages where it doubles as
  // a command keyword (id `kosong` → `empty me`). Safe to stand alone: `exit`
  // takes no arguments, so the single-keyword match can't swallow anything.
  bareKeyword: true,
};

/**
 * Pick command. Canonical hyperscript (hyperscript.org@0.9.93 PickCommand) is a
 * 6-variant selector, NOT the "random element from a collection" this schema
 * originally described:
 *
 *   pick [the]
 *     first  <count>            (of|from) <root>
 *     last   <count>            (of|from) <root>
 *     random [<count>]          (of|from) <root>
 *     (item|items|character|characters) <range> (of|from) <root>
 *         range: [at|from] (start|<expr>) [(to|..) (end|<expr>)] [inclusive|exclusive]
 *     match   [of] <re> [| flags] (of|from) <root>
 *     matches [of] <re> [| flags] (of|from) <root>
 *
 * The canonical variant/range model lives in the handcrafted English patterns
 * (`patterns/languages/en/pick.ts`) + the pick-range assembler
 * (`pattern-matcher.tryMatchPickRangeExpression`) + the pick AST mapper, NOT in
 * this schema. The `roles` below are deliberately FROZEN at
 * patient (the range/count/regex arg) + optional source (the `of`/`from` root):
 * they still drive the generated fallback pattern for all 24 languages, and
 * changing them would rewrite every language's generated pick pattern before the
 * per-language vocabulary exists (arc 2). Keep them byte-identical; extend `pick`
 * via patterns, not this schema.
 */
export const pickSchema: CommandSchema = {
  action: 'pick',
  description:
    'Select item(s), character(s), a range, first/last/random N, or regex matches from a root',
  category: 'variable',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The range/count/index/regex argument to pick',
      required: true,
      expectedTypes: ['literal', 'expression', 'reference'],
      svoPosition: 1,
      sovPosition: 1,
    },
    {
      role: 'source',
      description: 'The root to pick from (with "of"/"from" keyword)',
      required: false,
      expectedTypes: ['reference', 'expression'],
      svoPosition: 2,
      sovPosition: 2,
    },
  ],
};

/**
 * Scroll command: scrolls to a target element.
 *
 * Patterns:
 * - EN: scroll to #target
 * - ES: desplazar a #target
 * - JA: #target に スクロール
 *
 * Position modifiers (top/bottom/middle/center/nearest) and behavior flags
 * (smoothly/instantly) are accepted by the core parser as trailing tokens
 * but are not modeled here yet — the simple "scroll to <target>" form is the
 * gold path. Add as optional roles if/when tests demand it.
 */
export const scrollSchema: CommandSchema = {
  action: 'scroll',
  description: 'Scroll the viewport to a target element, or by an amount',
  category: 'navigation',
  primaryRole: 'destination',
  // `scroll [#box] [down] by 200px` (B9): the amount is required and the
  // destination absent; the element scrolled is the bare patient; both are
  // variant-only (`variantOnly`), so `scroll to {destination}` stays as it was. The direction
  // is the node's `scrollDirection`, read and written beside the amount
  // (tryScrollModifiers, the renderer), in the language's word (grammar-words).
  // Below the `to` patterns: where the amount is bare (tl, vi, uk), a variant
  // that outranked them read `sa #item` as the amount.
  requiredRoleVariants: [
    { role: 'quantity', omit: ['destination'], idSuffix: 'by', priorityDelta: -10 },
  ],
  // ScrollCommand's parseInput reads ONLY `raw.args` (`parsePosition` and
  // `resolveTarget` both walk that list) and throws when it is empty, so the
  // destination has to be an ARG. The blanket fallback mapping emitted
  // `modifiers.on`, which the command never consults — `scroll to #header`
  // failed with 'scroll command requires a target'.
  ast: { args: ['destination'] },
  // Position/behavior keywords appear in the parser's args alongside the target;
  // schema-driven role inference skips them when scanning for the destination.
  // Mirrors the runtime command's skip set in core/commands/navigation/scroll-to.ts.
  argSkipTokens: [
    'of',
    'the',
    'top',
    'bottom',
    'middle',
    'center',
    'nearest',
    'left',
    'right',
    'smoothly',
    'instantly',
  ],
  roles: [
    {
      role: 'destination',
      description: 'The element to scroll to',
      required: true,
      // 'expression' admits positional queries (`last <.message/> in #chat`).
      expectedTypes: ['selector', 'reference', 'expression'],
      svoPosition: 1,
      sovPosition: 1,
      markerOverride: { en: 'to' },
    },
    {
      role: 'patient',
      description:
        'The element scrolled by an amount (`scroll #box down by 200px`); the window when absent',
      required: false,
      // An element only: a bare word here would take the direction (`down`).
      expectedTypes: ['selector', 'reference'],
      variantOnly: true,
      svoPosition: 0.5,
      sovPosition: 0.5,
    },
    {
      role: 'quantity',
      description: 'The amount (`by 200px`)',
      required: false,
      expectedTypes: ['literal', 'expression', 'reference'],
      svoPosition: 2,
      sovPosition: 2,
      // As increment's: the by-marker languages, the rest bare (`#boxを 200px スクロール`).
      markerOverride: { en: 'by', es: 'por', pt: 'por', fr: 'par', de: 'um' },
      // `scroll to #x by 5` is no form: in the main pattern a bare trailing slot
      // took `in` from `scroll to #item in #box` (ar, zh).
      variantOnly: true,
    },
  ],
};

/**
 * `partials in` is a hyperscript-specific marker (no native translation in any
 * language); we use the English form across all 24 languages.
 */
const PARTIALS_IN_MARKER_ALL_LANGS: Record<string, string> = {
  en: 'partials in',
  es: 'partials in',
  pt: 'partials in',
  fr: 'partials in',
  de: 'partials in',
  it: 'partials in',
  ja: 'partials in',
  ko: 'partials in',
  zh: 'partials in',
  ar: 'partials in',
  he: 'partials in',
  hi: 'partials in',
  bn: 'partials in',
  tr: 'partials in',
  ru: 'partials in',
  uk: 'partials in',
  pl: 'partials in',
  id: 'partials in',
  vi: 'partials in',
  th: 'partials in',
  ms: 'partials in',
  tl: 'partials in',
  sw: 'partials in',
  qu: 'partials in',
};

/**
 * Push command: pushes a URL onto the browser history.
 *
 * Patterns:
 * - EN: push url "/page/2"
 * - ES: empujar url "/page/2"
 *
 * The literal `url` keyword is a required marker that disambiguates from any
 * other use of `push` and matches the core parser's required-keyword grammar.
 */
export const pushSchema: CommandSchema = {
  action: 'push',
  description: 'Push a URL onto the browser history',
  category: 'navigation',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The URL to push (after the `url` keyword)',
      required: true,
      expectedTypes: ['literal', 'expression'],
      svoPosition: 1,
      sovPosition: 1,
      markerOverride: URL_MARKER_ALL_LANGS,
    },
  ],
};

/**
 * Replace command: replaces the current entry in browser history.
 *
 * Patterns:
 * - EN: replace url "/new-path"
 * - ES: reemplazar url "/new-path"
 *
 * Same shape as push — the literal `url` keyword is required.
 */
export const replaceSchema: CommandSchema = {
  action: 'replace',
  description: 'Replace the current browser history entry with a URL',
  category: 'navigation',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The URL to replace with (after the `url` keyword)',
      required: true,
      expectedTypes: ['literal', 'expression'],
      svoPosition: 1,
      sovPosition: 1,
      markerOverride: URL_MARKER_ALL_LANGS,
    },
  ],
};

/**
 * Process command: processes hx-partial markup in HTML content.
 *
 * Patterns:
 * - EN: process partials in it
 * - EN: process partials in it using view transition
 *
 * Both `partials` and `in` are required marker keywords. The multi-word
 * marker is split into separate literal tokens by the pattern generator.
 */
export const processSchema: CommandSchema = {
  action: 'process',
  description: 'Process hx-partial markup in HTML content',
  category: 'dom-content',
  primaryRole: 'patient',
  // `process partials in it` → args ['it']; the optional tail rides as
  // `modifiers.viewTransition`, which ProcessPartialsCommand.parseInput has
  // read since #861 (`raw.modifiers?.viewTransition !== undefined`). Without a
  // descriptor this schema fell through to `buildGenericCommand`, whose
  // modifier list is fixed (destination/duration/method/style) and would have
  // dropped a captured `manner` on the floor.
  ast: { args: ['patient'], modifiers: { viewTransition: 'manner' } },
  roles: [
    {
      role: 'patient',
      description: 'The HTML content (string, element, or response) to process',
      required: true,
      expectedTypes: ['literal', 'reference', 'expression'],
      svoPosition: 1,
      sovPosition: 1,
      markerOverride: PARTIALS_IN_MARKER_ALL_LANGS,
    },
    {
      ...VIEW_TRANSITION_MANNER_ROLE,
      svoPosition: 2,
      sovPosition: 2,
    },
  ],
};

/**
 * Render command: renders a template with variables.
 * Syntax: render <template> / render <template> with <variables>
 */
export const renderSchema: CommandSchema = {
  action: 'render',
  description: 'Render a template with optional variables',
  category: 'dom-content',
  primaryRole: 'patient',
  roles: [
    {
      role: 'patient',
      description: 'The template to render',
      required: true,
      expectedTypes: ['selector', 'reference', 'expression'],
      svoPosition: 1,
      sovPosition: 2,
    },
    {
      role: 'style',
      description: 'Variables to pass to the template (with keyword)',
      required: false,
      expectedTypes: ['expression'],
      valueShape: 'object',
      svoPosition: 2,
      sovPosition: 1,
    },
  ],
};

// =============================================================================
// Schema Registry
// =============================================================================

/**
 * All available command schemas.
 */
export const commandSchemas: Record<ActionType, CommandSchema> = {
  // Original schemas
  toggle: toggleSchema,
  add: addSchema,
  remove: removeSchema,
  put: putSchema,
  set: setSchema,
  show: showSchema,
  hide: hideSchema,
  on: onSchema,
  trigger: triggerSchema,
  wait: waitSchema,
  fetch: fetchSchema,
  increment: incrementSchema,
  decrement: decrementSchema,
  append: appendSchema,
  prepend: prependSchema,
  // Batch 1 - Simple Commands
  log: logSchema,
  get: getCommandSchema,
  take: takeSchema,
  make: makeSchema,
  halt: haltSchema,
  settle: settleSchema,
  throw: throwSchema,
  send: sendSchema,
  // Batch 2 - Control Flow
  if: ifSchema,
  unless: unlessSchema,
  else: elseSchema,
  repeat: repeatSchema,
  for: forSchema,
  while: whileSchema,
  continue: continueSchema,
  // Batch 3 - DOM & Navigation
  go: goSchema,
  scroll: scrollSchema,
  push: pushSchema,
  replace: replaceSchema,
  process: processSchema,
  transition: transitionSchema,
  viewTransition: viewTransitionSchema,
  clone: cloneSchema,
  focus: focusSchema,
  blur: blurSchema,
  empty: emptySchema,
  open: openSchema,
  close: closeSchema,
  select: selectSchema,
  clear: clearSchema,
  reset: resetSchema,
  breakpoint: breakpointSchema,
  // Batch 4 - Advanced
  call: callSchema,
  return: returnSchema,
  js: jsSchema,
  tell: tellSchema,
  default: defaultSchema,
  init: initSchema,
  behavior: behaviorSchema,
  install: installSchema,
  measure: measureSchema,
  // Batch 5 - DOM Content Manipulation
  swap: swapSchema,
  morph: morphSchema,
  // Batch 6 - Missing Commands
  beep: beepSchema,
  ask: askSchema,
  answer: answerSchema,
  break: breakSchema,
  copy: copySchema,
  exit: exitSchema,
  pick: pickSchema,
  render: renderSchema,
  // Reactivity
  bind: bindSchema,
  live: liveSchema,
  when: whenSchema,
  // Realtime / streaming + service workers
  eventsource: eventsourceSchema,
  socket: socketSchema,
  worker: workerSchema,
  intercept: interceptSchema,
  // Meta commands (for compound structures)
  compound: {
    action: 'compound',
    description: 'A compound node representing chained statements',
    primaryRole: 'patient', // Compound nodes don't have a traditional primary role
    category: 'control-flow',
    hasBody: true,
    roles: [],
  },
};

/**
 * Get a command schema by action type.
 */
export function getSchema(action: ActionType): CommandSchema | undefined {
  return commandSchemas[action];
}

/**
 * Get all schemas for a category.
 */
export function getSchemasByCategory(category: CommandCategory): CommandSchema[] {
  return Object.values(commandSchemas).filter(s => s.category === category);
}

/**
 * Get all fully-defined schemas (with roles).
 */
export function getDefinedSchemas(): CommandSchema[] {
  return Object.values(commandSchemas).filter(
    s => s.structuralOnly !== true && (s.roles.length > 0 || s.bareKeyword === true)
  );
}

// =============================================================================
// Schema Validation (Development Only)
// =============================================================================

// Run schema validation at module load time when explicitly requested.
//
// These diagnostics are for people editing the schemas in this package, but they
// used to fire for every consumer merely importing it (~44 lines of stderr on a
// bare `import '@lokascript/domain-llm'`, in every downstream test run and CI log).
// They are opt-in via LOKASCRIPT_SCHEMA_VALIDATION=1; the standing check that
// keeps schemas honest is `schema-validation.test.ts` in this package.
if (typeof process !== 'undefined' && process.env.LOKASCRIPT_SCHEMA_VALIDATION === '1') {
  // Dynamic import to avoid bundling in production
  import('./schema-validator')
    .then(({ validateAllSchemas, formatValidationResults }) => {
      const validations = validateAllSchemas(commandSchemas);

      if (validations.size > 0) {
        console.warn('[SCHEMA VALIDATION] Found issues in command schemas:');
        console.warn(formatValidationResults(validations));
        console.warn('\nThese warnings help identify potential schema design issues.');
        console.warn('Fix them to improve type inference and avoid runtime bugs.');
      }
    })
    .catch(err => {
      // Silently ignore if schema validator is not available
      console.debug('Schema validation skipped:', err);
    });
}
